import Add from '@mui/icons-material/Add';
import Call from '@mui/icons-material/Call';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ContactEmergencyIcon from '@mui/icons-material/ContactPhone';
import Delete from '@mui/icons-material/Delete';
import Done from '@mui/icons-material/Done';
import Edit from '@mui/icons-material/Edit';
import ExtensionIcon from '@mui/icons-material/Extension';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import InfoIcon from '@mui/icons-material/Info';
import PsychologyIcon from '@mui/icons-material/Psychology';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ReorderIcon from '@mui/icons-material/Reorder';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import SettingsPhone from '@mui/icons-material/SettingsPhone';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import VoiceOverOffIcon from '@mui/icons-material/VoiceOverOff';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Button,
  Checkbox,
  Fade,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  TextField,
  Theme,
  Tooltip,
  TooltipProps,
} from '@mui/material';
import { blue, green } from '@mui/material/colors';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import makeStyles from '@mui/styles/makeStyles';
import { GridRenderCellParams } from '@mui/x-data-grid';
import clsx from 'clsx';
import ct from 'countries-and-timezones';
import { Formik, FormikProps } from 'formik';
import { DateTime, Duration } from 'luxon';
import moment from 'moment-timezone';
import React, {
  ReactChild,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cookie from 'react-cookies';
import { useMutation } from 'react-query';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useGetBusiness2Query } from 'src/api/apigQuery';
import {
  CRMDisplayNameMap,
  CRMIDMap,
  CRMNameMap,
} from 'src/app/constants/CRMNameMap';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from 'src/app/modules/Manage/pages/queues-table/DataTable';
import { LabelledOutline } from 'src/common/final-form-material-ui';
import { trackModalUpdates } from 'src/common/userTracking';
import { useFeatureFlag } from 'src/app/hooks/useFeatureFlag';

import Axios from '../../../common/axiosInterceptor';
import {
  FormikSelect,
  FormikTextField,
  MaterialAutocomplete,
} from '../../../common/formik-material-ui';
import { FormikSwitch } from '../../../common/formik-material-ui/FormikSwitch';
import { isUrl } from '../../../common/validators';
import { ISchedulableHours } from '../../../lib/kixie-entities/interfaces/ISchedulableHours';
import { Business } from '../../../lib/kixie-entities/tables/Business';
import { BusinessHarassmentPolicy } from '../../../lib/kixie-entities/tables/BusinessHarassmentPolicy';
import { IUserAtom, IUserModel, Role, user } from '../../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../../recoil/selectors/makePHPRequest';
import {
  getIsDeveloper,
  IPermissions,
  permissionsSelector,
} from '../../../recoil/selectors/permissionsSelector';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { ConfirmAgentPowerlists } from '../../components/modals/ConfirmAgentPowerlists';
import dataTableScss from '../../modules/Manage/pages/queues-table/data-table.module.css';
import { EnhancedTableToolbar } from '../../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import {
  DEFAULT_MACHINE_DETECTION_TIME_MS,
  MINIMUM_DURATION_MINS,
  POWERDIALER_MACHINE_DETECT_PRODUCT_ID,
} from '../../pages/ManageAccount';
import {
  getCRMIcon,
  getCRMName,
} from '../../pages/onboardingWalkthrough/AccountSettings';
import { CustomCRM } from '../../subpages/CustomCRM';
import {
  ICustomCRM,
  TFormikErrorsForICustomCRM,
  webhooksComponent,
} from '../../subpages/CustomCrmInputs';
import AIOSettingsTab from '../accountSettings/AIOSettingsTab';
import { HarassmentRules } from '../CadenceSettings/HarassmentRules';
import { createHoursString, Scheduler } from '../CadenceSettings/Scheduler';
import {
  DefaultAction,
  DefaultActionSkeleton,
  getDefaultActionPayload2,
  getDefaultActionUrl,
} from '../dropdowns/DefaultAction';
import TabLabel from '../general/TabLabel';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';
import { RecordNow } from '../recorder/RecordNow';
import { Subcontainer } from '../subcontainer/Subcontainer';

import { getErrorTabIndex, isErrorInTab } from './DevicesAndRoutingModal';
import { ModalWithStyles } from './ModalWithStyles';
import { WizardModal } from './WizardModal';

//eslint-disable-next-line
const urlRegex = /(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;

export const calculateTimeDifference = (startDate, endDate) => {
  // Parse startDate and endDate or use the current time for endDate if undefined
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  // Calculate the time difference in milliseconds
  const differenceInMs = end.getTime() - start.getTime();

  if (differenceInMs < 0) {
    throw new Error('endDate must be later than startDate');
  }

  // Convert milliseconds to hours, minutes, and seconds
  const hours = Math.floor(differenceInMs / (1000 * 60 * 60));
  const minutes = Math.floor((differenceInMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((differenceInMs % (1000 * 60)) / 1000);

  // Format as hh:mm:ss
  const formattedTime = [
    String(hours).padStart(2, '0'),
    String(minutes).padStart(2, '0'),
    String(seconds).padStart(2, '0'),
  ].join(':');

  return formattedTime;
};

export const formatDate = (isoString) => {
  const date = new Date(isoString);
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const monthName = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  return `${monthName} ${day}, ${year}`;
};
export const getDurationInHMS = (startDate: string, endDate: string) => {};

export const InfoTooltip = (props: Omit<TooltipProps, 'children'>) => {
  return (
    <Tooltip
      style={{ cursor: 'pointer' }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 600 }}
      placement="right-start"
      {...props}
    >
      <InfoIcon color="inherit" />
    </Tooltip>
  );
};

export interface IUsagePlan {
  active: number;
  agentSeat: number;
  burstLimit: number;
  canHaveDiscount: number;
  created: string;
  flatfee: number;
  monthToMonth: number;
  perUseproductid: number | string | null;
  product: string;
  productId: number;
  productid: number;
  quotaLimit: number;
  quotaPeriod: string;
  rateLimit: number;
  retail: number;
  throttlingEnabled: number;
  updated: string;
  usagePlanActive: number;
  usagePlanAwsJson: string;
  usagePlanId: number;
}

export interface IAIOSettings {
  intentFieldName: string;
  intentFieldLabel: string;
  intentScoreThreshold: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  tab: {
    maxWidth: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
    '&[tabindex="0"]': {
      backgroundColor: theme.palette.divider,
    },
    '&[name="Super Admin Settings"]': {
      border: '3px solid #ffec61',
    },
    '&[name="MaestroAI"]': {
      border: '3px solid #ffec61',
    },
  },
  tabs: {},
  tabIcon: {
    marginRight: 15,
  },
  header: {
    color: 'var(--primary)', // pull color from primary
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    marginRight: theme.spacing(1),
    maxWidth: '87%',
  },
  modal: {
    paddingBottom: 0,
    maxHeight: 'calc(100vh - 210px)',
    overflowY: 'auto',
  },
  defaultAction: {
    marginTop: theme.spacing(2),
  },
  warningIcon: {
    color: 'orange',
  },
  searchBar: {
    padding: '0px',
    '& #searchBox': {
      width: '330px',
    },
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  spinner: {
    position: 'fixed !important' as any,
  },
  selectMenuPaper: {
    maxHeight: 460, //'calc(100% - 96px)'
  },
  outbound2: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    flexGrow: 1,
    width: '100%',
  },
  borderOutline: {
    border: `1px solid ${
      theme?.palette?.mode === 'dark' ? 'grey' : 'lightgrey'
    }`,
    width: '52px',
  },
  borderOutline2: {
    borderTop: 'none',
    backgroundColor: theme?.palette?.mode === 'dark' ? 'dimgray' : 'ghostwhite',
    minHeight: '56px',
    border: `1px solid ${
      theme?.palette?.mode === 'dark' ? 'grey' : 'lightgrey'
    }`,
  },
  billing: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    flexGrow: 1,
    width: '100%',
  },
  dealButton: {
    backgroundColor: 'rgb(104,71,255)',
    fontWeight: 700,
    '&:hover': {
      backgroundColor: 'rgb(124,91,255)',
    },
    '&:focus': {
      backgroundColor: 'rgb(134,101,255)',
    },
    '&:disabled': {
      backgroundColor: '#e0e0e0',
    },
  },
  editButton: {
    maxWidth: '155px',
    maxHeight: '50px',
    minWidth: '155px',
    minHeight: '50px',
  },
  fieldRow: {
    display: 'flex',
    marginLeft: '2px',
    marginRight: '2px',
  },
  alignCenter: {
    alignItems: 'center',
  },
  fullWidthSelect: {
    width: '100%',
    '& .MuiFormControl-marginDense': {
      width: '100%',
    },
  },
  propertiesContainer: {
    border: `1px solid ${
      theme?.palette?.mode === 'dark' ? 'grey' : 'lightgrey'
    }`,
    width: '90%',
    height: '250px',
    overflow: 'scroll',
    borderRadius: '4px',
    margin: '0px 36px 24px',
    padding: '12px',
  },
  crmPropertyCheckbox: {
    '&.Mui-checked': {
      color: 'rgb(104,71,255) !important',
    },
  },
  crmPropertyCheckboxDisabled: {
    '&.Mui-checked': {
      color: 'rgb(154,171,155) !important',
    },
  },
  checkbox: {
    userSelect: 'none',
  },
  listContainer: {
    '& > div > div': {
      paddingBottom: 0,
      paddingRight: theme.spacing(0.5),
    },
  },
  list: {
    listStyleType: 'none',
    padding: 0,
    marginBottom: 0,
  },
  listItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  listItemTextField: {
    flex: 1,
  },
  controls: {
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: theme.spacing(0.5),
  },
  editBtn: {
    padding: theme.spacing(0.5),
    minHeight: 0,
    minWidth: 0,
  },
  deleteBtn: {
    color: 'red',
    padding: theme.spacing(0.5),
    minHeight: 0,
    minWidth: 0,
  },
  addBtn: {
    marginBottom: 0,
    userSelect: 'none',
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  mutedHeader: {
    fontSize: '10px',
    color: '#666666',
    display: 'block',
    margin: 0,
  },
  fourCol: {
    flex: '1 1 33.333%',
    display: 'inline-flex',
    flexDirection: 'column',
    flexGrow: 0,
    padding: '5px 0',
  },
}));

const emptyHoursObject = {
  1: {
    day: 'Monday',
    hours: [],
  },
  2: {
    day: 'Tuesday',
    hours: [],
  },
  3: {
    day: 'Wednesday',
    hours: [],
  },
  4: {
    day: 'Thursday',
    hours: [],
  },
  5: {
    day: 'Friday',
    hours: [],
  },
  6: {
    day: 'Saturday',
    hours: [],
  },
  7: {
    day: 'Sunday',
    hours: [],
  },
};

const CALL_TRANSCRIPTION_MAX_PHRASE_CHARACTERS = 1000;

const CALL_TRANSCRIPTION_MAX_KEYWORD_CHARACTERS = 200;

const CALL_TRANSCRIPTION_MIN_CALL_DURATION_MINUTES = 2;
const CALL_TRANSCRIPTION_MAX_CALL_DURATION_MINUTES = 180;

const OUTREACH_CRM_ID = 26;

export const getCallTranscriptionSettingsEndpoint = (userAtom): string => {
  const accessToken = cookie.load('accessToken');
  if (!accessToken || !userAtom.businessid) {
    return '';
  }

  return `${endpointEnum.CALL_TRANSCRIPTION_SETTINGS.replace(
    '{businessId}',
    userAtom.businessid.toString()
  )}?accessToken=${accessToken}`;
};

export const CALL_TRANSCRIPTION_SETTINGS_STAGE = 'itn';

export interface ICallTranscriptionSettings {
  minimumDuration: number | undefined;
  isCallStrengthKeywordsEnabled: boolean;
  isCallStrengthPhrasesEnabled: boolean;
  isCallStrengthCallSentimentEnabled: boolean;
  phrases: string[];
  keywords: string[];
}

const requiredIfEnabled = (fieldName: string) => ({
  name: 'required',
  message: 'Required',
  test: function (value: any) {
    return !(this as any).parent[fieldName] || Boolean(value);
  },
});

// create shortName
const timezones: Record<
  string,
  {
    name: string;
    shortName: string;
    utcOffsetStr: string;
  }
> = Object.values(ct.getAllTimezones() as { [name: string]: any }) // get all timezones
  .reduce((allzones: any, timezone: any) => {
    allzones[timezone.name] = {
      ...timezone,
      shortName: timezone.name
        .replace(/^(.+\/)*([^/]+)$/g, '$2')
        .replace(/[_]/g, ' '),
    };
    return allzones;
  }, {});

// sort By Name
// and filter out gmt
const timezonesDropdown = Object.values(timezones)
  .filter(
    (timezone) =>
      !timezone.name.startsWith('Etc/') &&
      !['CTU', 'UTC', 'GMT', 'GMT0', 'GMT+0', 'GMT-0'].includes(timezone.name)
  ) // filter out ect/GMT
  .map((timezone) => timezone.name)
  .sort();
// or sort by offset first then by Name (either this option or the sort by name option above)
// .sort((tzLeft, tzRight) => tzLeft.utcOffsetStr === tzRight.utcOffsetStr ? (tzLeft.name < tzRight.name ? -1 : 1) : (tzLeft.utcOffsetStr < tzRight.utcOffsetStr ? -1 : 1)),

const filterOptions = createFilterOptions({
  stringify: (option: string) => option.replace('_', ' ').replace('/', ' '),
});

interface IEditableText {
  id: string;
  text: string;
  isEditing: boolean;
  touched: boolean;
}

export function ManageAccountModal(props: {
  show: boolean;
  onHide: () => void;
  tabIndex?: number;
  setUsagePlan?: (usagePlan: string) => void;
}) {
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const classes = useStyles();
  const formikTouched = useRef<null | any>(null);
  const formikRef = useRef<null | any>(null);
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const userAtom = userAtomLoadable.contents as IUserAtom;
  const [tabIndex, setTabIndex] = useState(props.tabIndex || 0);
  const [activeCRM, setActiveCRM] = useState<number | string>('active');
  const [importLoading, setImportLoading] = useState<boolean>(false);
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const {
    role,
    canAccessPowerlistProduct,
    canSetApiLevel,
    canViewAllUsagePlans,
    isBusinessEligibleToAccessCallTranscriptions,
    isMachineDetectionFeatureEnabled,
  } = permissionsLoadable.contents as IPermissions;

  const isCallTranscriptionsFeaturedEnabled =
    process.env.VITE_APP_FEATURE_CALL_TRANSCRIPTIONS === '1' &&
    isBusinessEligibleToAccessCallTranscriptions === true;
  const isCrmPropertiesFeatureEnabled =
    process.env.VITE_APP_CRM_PROPERTIES === '1';

  const { treatment: aioTreatment } = useFeatureFlag('AIO-159-signups');
  const isAIOEnabled =
    process.env.VITE_APP_AIO === '1' && role === Role.SUPERADMIN && ['on', 'control'].includes(aioTreatment);

  const AIO_SETTINGS_TAB_INDEX = (
    7 + (isCrmPropertiesFeatureEnabled ? 1 : 0)
  ).toString();

  const CUSTOM_CRM_TAB_INDEX = (
    7 +
    (isCrmPropertiesFeatureEnabled ? 1 : 0) +
    (isAIOEnabled ? 1 : 0)
  ).toString();

  const LOCAL_PRESENCE_ROUTING_TAB_INDEX = (
    8 +
    (isCrmPropertiesFeatureEnabled ? 1 : 0) +
    (isAIOEnabled ? 1 : 0)
  ).toString();

  const CALL_TRANSCRIPTION_SETTINGS_TAB_INDEX = (
    9 +
    (isCrmPropertiesFeatureEnabled ? 1 : 0) +
    (isAIOEnabled ? 1 : 0)
  ).toString();

  const SUPER_ADMIN_SETTINGS_TAB_INDEX = (
    9 +
    (isCrmPropertiesFeatureEnabled ? 1 : 0) +
    (isAIOEnabled ? 1 : 0) +
    (isCallTranscriptionsFeaturedEnabled ? 1 : 0)
  ).toString();

  const [
    _,
    getBusinessCRMPropertiesLoadable,
    setRefetchToggleForGetBusinessCRMProperties,
  ] = makeApiRequestCached(
    {
      method: 'post',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      payloadJsonString: JSON.stringify({
        search: activeCRM,
      }),
      endpoint: endpointEnum.BUSINESS_CRM + `/${userAtom.businessid}`,
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.data ?? cached.current
  );

  // state for business hours modal;
  const [showBusinessHours, setShowBusinessHours] = useState(false);
  const handleShowBusinessHours = () => setShowBusinessHours(true);
  const [selectedRows, setSelectedRows] = useState<{
    [key: string]: boolean;
  }>({});
  const [phoneRows, setPhoneRows] = useState<{
    [key: string]: boolean;
  }>({});
  const [selectedRowsSearch, setSelectedRowsSearch] = useState<{
    [key: string]: boolean;
  }>({});
  const onHideBusinessHours = () => setShowBusinessHours(false);
  const [hoursString, setHoursString] = useState('');
  const [showCloseDealConfirmation, setShowCloseDealConfirmation] = useState(
    false
  );
  const [submittingCloseDeal, setSubmittingCloseDeal] = useState(false);
  const [usageGrandfathered, setUsageGrandfathered] = useState(false);
  const [currentUsagePlan, setCurrentUsagePlan] = useState<
    IUsagePlan | undefined
  >();
  const splitHours = hoursString.split(',');
  const schedulableHoursCached = useRef<ISchedulableHours>();
  const handleSetSchedulableHours = (hours: ISchedulableHours) => {
    schedulableHoursCached.current = hours;
  };

  const openCloseDealConfirmation = () => {
    setShowCloseDealConfirmation(true);
  };

  const hideCloseDealConfirmation = () => {
    setShowCloseDealConfirmation(false);
  };

  const [showConfirmAgentPowerlists, setShowConfirmHandlePowerlists] = useState<
    boolean
  >(false);

  const handleChange = (_formik: FormikProps<any>) => async (
    _event: any,
    newIndex: number
  ) => {
    setTabIndex(+newIndex);
  };
  const mutation = useMutation(getApiMutation(userAtom, setUserAtom));
  const {
    data: businessData,
    isFetching: isFetchingBusinessData,
    refetch: refetchBusinessData,
  } = useGetBusiness2Query(
    {
      userSettings: {
        apikey: userAtom.apikey,
        businessId: userAtom.businessid,
        businessid: userAtom.businessid,
        userId: userAtom.userid,
        userid: userAtom.userid,
      },
    },
    { skip: !userAtom.businessid }
  );

  const [showOutcomeForAllAgentsVal, setShowOutcomeForAllAgentsVal] = useState(
    businessData?.showOutcomeForAllAgents === 1
  );
  const [showDiscountEndDate, setShowDiscountEndDate] = useState(false);

  const [hasBusinessUsersOauthedCached] = makeApiRequestCached<
    number | undefined
  >(
    {
      endpoint: endpointEnum.GET_HAS_USERS_OAUTHED,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_HAS_USERS_OAUTHED,
        crmid: OUTREACH_CRM_ID,
      }),
    },
    (loadable, cached) => loadable.contents?.response?.data ?? cached.current
  );
  const businessHasOauthedWithOutreach =
    hasBusinessUsersOauthedCached?.current &&
    hasBusinessUsersOauthedCached?.current > 0;

  useEffect(() => {
    if (businessData?.discountEndDate) {
      setShowDiscountEndDate(true);
    }
  }, [businessData?.discountEndDate]);

  const initialAllowAgentPowerlists = businessData?.allowAgentPowerlist;
  const [
    businessUsersActiveCached,
    businessUsersActiveLoadable,
  ] = makeApiRequestCached<IUserModel[]>(
    {
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    },
    (loadable, cached) =>
      (loadable.contents?.response?.data?.result as IUserModel[])
        ?.filter((user) => user.isactive === 1)
        .sort((a, b) =>
          a?.fname?.toLowerCase()?.localeCompare(b?.fname?.toLowerCase())
        ) ?? cached.current
  );

  const currentDate = DateTime.now().toSQLDate();

  const defaultCrmUser = useMemo(
    () =>
      businessData?.defaultCrmUserid
        ? businessUsersActiveCached.current?.find(
            (user) =>
              Number(user.userid) === Number(businessData?.defaultCrmUserid)
          )
        : {
            fname: 'None',
            lname: '',
            userid: '',
          },
    [businessData, businessUsersActiveLoadable]
  );

  const [businessCached, businessLoadable] = makeApiRequestCached<Business>(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${userAtom.businessid}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.[0] ?? cached.current ?? []
  );

  const activeBusinessCRMsLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: 'legacy/getActiveBusinessCRMs',
      payloadJsonString: JSON.stringify({
        call: 'getActiveBusinessCRMs',
      }),
    })
  );

  const activeCRMs = useMemo(() => {
    const crmIds =
      activeBusinessCRMsLoadable?.contents?.response?.data?.result ?? [];
    let activeCRMsArray = crmIds.map((id: { crmID: number }) => {
      return {
        value: CRMNameMap[id.crmID],
        displayName: CRMDisplayNameMap[CRMNameMap[id.crmID]],
      };
    });
    activeCRMsArray.push({
      value: 'none',
      displayName: 'None',
    });
    activeCRMsArray = activeCRMsArray.filter((elem) => {
      return !!elem.displayName && !!elem.value;
    });
    return activeCRMsArray;
  }, [activeBusinessCRMsLoadable?.contents?.response?.data?.result]);

  const billingEmailLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: 'legacy/getStripeBillingEmail',
      payloadJsonString: JSON.stringify({
        call: 'getStripeBillingEmail',
      }),
    })
  );

  const [
    usagePlansCached,
    usagePlansLoadable,
    setRefetchToggleForUsagePlans,
  ] = makeApiRequestCached<
    {
      product: string;
      active: 0 | 1;
      quotaLimit: number;
      quotaPeriod: 'day' | 'week' | 'month';
      perUseproductid: number;
    }[]
  >(
    {
      endpoint: endpointEnum.GET_USAGE_PLANS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_USAGE_PLANS,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data
        ?.filter(
          (plan) =>
            plan.product === '1k_daily_api_requests' ||
            plan.product === '10k_daily_api_requests' ||
            plan.product === '10k_grandfathered_daily_api_requests'
          // || plan.product.endsWith('_paid_daily_api_requests')
        )
        ?.sort((a, b) => a.quotaLimit - b.quotaLimit) ??
      cached.current ??
      []
  );

  useEffect(() => {
    setRefetchToggleForBusinessDataV1({});
    setRefetchToggleForUsagePlans({});
    setRefetchToggleForGetBusinessCRMProperties({});
  }, []);
  const setRefetchToggleForBusinessData = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_DATA)
  );
  const setRefetchToggleForBusinessDataV1 = useSetRecoilState(
    refetchToggle(`${endpointEnum.GET_BUSINESSES}/${userAtom.businessid}`)
  );
  const setRefetchToggleForCustomCRM = useSetRecoilState(
    refetchToggle(endpointEnum.GET_CUSTOM_CRM)
  );
  const setRefetchToggleForCallTranscriptionSettings = useSetRecoilState(
    refetchToggle(getCallTranscriptionSettingsEndpoint(userAtom))
  );
  const [filenames, setFilenames] = useState({
    holdMusic: businessData?.holdMusic ?? '',
  });

  const [uploadedFiles, setUploadedFiles] = useState<any>({});
  const [fileSizeError, setFileSizeError] = useState<boolean>(false);
  const [filepaths, setFilepaths] = useState({
    holdMusic: businessData?.holdMusic ?? '',
  });
  const crmData = {
    businessid: userAtom?.businessid,
    call: methodNameEnum.GET_CUSTOM_CRM,
    apikey: userAtom?.apikey,
  };
  const customCrmLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_CUSTOM_CRM,
      method: 'POST',
      payloadJsonString: JSON.stringify(crmData),
    })
  );
  const crmDataMemo = useMemo(
    () => customCrmLoadable?.contents?.response?.data?.result?.[0] ?? {},
    [customCrmLoadable]
  );

  const crmStats = {
    businessid: userAtom?.businessid,
    call: methodNameEnum.GET_BUSINESS_CRM_STATS,
    apikey: userAtom?.apikey,
  };
  const crmStatsLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_CRM_STATS,
      method: 'POST',
      payloadJsonString: JSON.stringify(crmStats),
    })
  );
  const crmStatsMemo = useMemo(
    () => crmStatsLoadable?.contents?.response?.data?.result ?? {},
    [crmStatsLoadable]
  );

  const onChangeFile = (type: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    setFileSizeError(false);
    if (files && files[0].size > 20_000_000) {
      setFileSizeError(true);
    }
    setFilenames({
      ...filenames,
      // voicemail or greeting
      [type]:
        (files?.length ?? 0) > 1
          ? `${files?.length} files`
          : files?.[0]?.name ?? '',
    });
    setFilepaths({
      ...filepaths,
      [type]: files?.[0] && URL.createObjectURL(files?.[0]), // create file blob
    });
    setUploadedFiles({
      ...uploadedFiles,
      [type]: files,
    }); // take first file
  };

  const handleRecording = (type: string) => (files: Record<string, any>) => {
    setUploadedFiles({
      ...uploadedFiles,
      [type]: files,
    });
  };

  useEffect(() => {
    setFilenames({
      holdMusic: isUrl(businessData?.holdMusic ?? '')
        ? businessData?.holdMusic?.split('/').slice(-1).pop() ?? ''
        : '', // filename from uri/path
    });
    setFilepaths({
      holdMusic: businessData?.holdMusic ?? '',
    });
  }, [businessData]);

  const tabNames = useMemo(() => {
    const tabs = [
      { title: 'Settings', icon: SettingsApplicationsIcon },
      { title: 'Call Options', icon: SettingsPhone },
      { title: 'Manager Options', icon: ReorderIcon },
      { title: 'Integrations', icon: ExtensionIcon },
      { title: 'Connected CRMs', icon: CloudSyncIcon },
      { title: 'Business Hours', icon: QueryBuilderIcon },
      { title: 'Harassment Rules', icon: VoiceOverOffIcon },
    ];

    if (isCrmPropertiesFeatureEnabled) {
      tabs.push({ title: 'CRM Properties', icon: ContactEmergencyIcon });
    }

    if (isAIOEnabled) {
      tabs.push({ title: 'MaestroAI', icon: PsychologyIcon });
    }

    tabs.push({ title: 'Custom CRM', icon: ImportExportIcon });

    tabs.push({ title: 'LOCAL PRESENCE ROUTING', icon: CallSplitIcon });

    if (isCallTranscriptionsFeaturedEnabled) {
      tabs.push({ title: 'Conversation Intelligence', icon: Call });
    }

    if (role === Role.SUPERADMIN) {
      tabs.push({ title: 'Super Admin Settings', icon: SupervisorAccountIcon });
    }

    return tabs;
  }, [
    isCallTranscriptionsFeaturedEnabled,
    role,
    isCrmPropertiesFeatureEnabled,
    isAIOEnabled,
  ]);

  const currentTabTitle = useMemo(() => {
    return tabNames[tabIndex]?.title ?? '';
  }, [tabIndex, tabNames]);

  const tabToFields = [
    ['accountSettings'], // Settings
    ['callOptions'], // Call Options
    ['managerOptions'], // Manager Options
    ['api'], // Integrations
    ['connectedCRMs'], // Connected CRMs
    ['businessHours'], // Business Hours
    ['businessHarassmentPolicies'], // Harassment Rules
    ['crmProperties'], // CRM Properties
    ['aioSettings'], // AI Orchestration
    ['customCRM'], // Custom CRM
    [], // LOCAL PRESENCE ROUTING
    [], // Conversation Intelligence
    ['defaultCrmUserid'], // Super Admin Settings
  ];

  const commonValidation = {
    accountSettings: Yup.object({
      businessName: Yup.string().required('Required'),
      businessDiscount: Yup.number().max(
        60,
        'Must be less than or equal to 60'
      ),
      connectionDuration: Yup.number().max(
        1000,
        'Must be less than or equal to 1000 seconds'
      ),
      timezone: Yup.string().required('Required'),
    }),
    submitHoldMusicMp3File: Yup.object({
      name: Yup.string().checkFileSize(
        20_000_000,
        'File must be at or below 20mb'
      ),
    }),
    defaultCrmUserid: businessData?.defaultCrmUserid
      ? Yup.string().required('Required')
      : Yup.string(),
    aioSettings: Yup.object({
      intentFieldName: Yup.string().max(
        100,
        'Must be less than or equal to 100 characters'
      ),
      intentFieldLabel: Yup.string().max(
        100,
        'Must be less than or equal to 100 characters'
      ),
      intentScoreThreshold: Yup.number()
        .min(0, 'Must be greater than or equal to 0')
        .max(100, 'Must be less than or equal to 100'),
    }),
  };

  const customCrmValidation = {
    customCRM: Yup.object({
      crmName: Yup.string()
        .max(100, 'Must be less than or equal to 100 characters')
        .test(requiredIfEnabled('hasCustomCRM')),
      searchEndpoint: Yup.string()
        .max(500, 'Must be less than or equal to 500 characters')
        .matches(urlRegex, 'Must be a valid url')
        .test(requiredIfEnabled('hasCustomCRM')),
      callsWebhook: Yup.string()
        .max(500, 'Must be less than or equal to 500 characters')
        .matches(urlRegex, 'Must be a valid url'),
      smsWebhook: Yup.string()
        .max(500, 'Must be less than or equal to 500 characters')
        .matches(urlRegex, 'Must be a valid url'),
      contactUpdateEndpoint: Yup.string()
        .max(500, 'Must be less than or equal to 500 characters')
        .matches(urlRegex, 'Must be a valid url'),
      dispositionEndpoint: Yup.string()
        .max(500, 'Must be less than or equal to 500 characters')
        .matches(urlRegex, 'Must be a valid url'),
      activityEndpoint: Yup.string()
        .max(500, 'Must be less than or equal to 500 characters')
        .matches(urlRegex, 'Must be a valid url'),
      headers: Yup.array().of(
        Yup.object().shape(
          {
            name: Yup.string()
              .transform((curr, orig) => (orig === '' ? null : curr))
              .when(['value'], {
                is: (value, name) => !name && value,
                then: Yup.string().required('Name is required'),
              }),
            value: Yup.string()
              .transform((curr, orig) => (orig === '' ? null : curr))
              .when(['name'], {
                is: (name, value) => !value && name,
                then: Yup.string().required('Value is required'),
              }),
          },
          [['value', 'name']]
        )
      ),
    }),
  };

  const validationSchema = Yup.object({
    ...commonValidation,
  });

  const validationSchemaForSuperAdmin = Yup.object({
    ...commonValidation,
    ...customCrmValidation,
  });

  const getCurrentUsagePlan = (apiKeyObj: any) => {
    const currentPlan = usagePlansCached.current.find(
      (plan) =>
        plan?.product === apiKeyObj?.[`${process.env.VITE_APP_REGION}`]?.product
    );
    // Determine if a BID is on a grandfathered in plan
    setUsageGrandfathered(
      String(currentPlan?.product)?.toLowerCase()?.includes('grandfather') ??
        false
    );
    setCurrentUsagePlan(currentPlan as IUsagePlan);
    return currentPlan?.product ?? '';
  };

  const importedrows = useMemo(() => {
    if (getBusinessCRMPropertiesLoadable?.contents?.response?.data?.[0]) {
      try {
        const propertieObj =
          JSON.parse(
            getBusinessCRMPropertiesLoadable?.contents?.response?.data?.[0]
              ?.properties
          ) ?? {};
        let properties = Object.keys(propertieObj).map((crmProperty) => {
          if (crmProperty) {
            return {
              ...propertieObj[crmProperty],
            };
          }
        });
        properties = properties.filter((elem) => elem?.name);
        properties = properties.sort(
          (a, b) => (a?.displayOrder ?? -1) - (b?.displayOrder ?? -1)
        );
        return properties;
      } catch (e) {
        // console.log('crm properties error', e);
        return [];
      }
    }
    return [];
  }, [getBusinessCRMPropertiesLoadable]);

  const rows: {
    id: string | number;
    crm: string;
    property: string;
    label: string;
    defaultPhone?: boolean;
    importdatetime: string;
    phone: number;
    selected: 0 | 1;
  }[] = useMemo(() => {
    if (importedrows?.length) {
      try {
        const crmName =
          getBusinessCRMPropertiesLoadable?.contents?.response?.data?.[0]
            ?.crmName ?? '';
        const properties = importedrows.map((elem) => {
          return {
            id: elem.id,
            crm: crmName,
            defaultPhone: elem.defaultPhone,
            property: elem.name,
            label: elem.label,
            importdatetime: elem?.updatedDateTime ?? '',
            phone: elem?.phone || elem?.defaultPhone ? 1 : 0,
            selected: elem?.selected ?? 0,
          };
        });
        return properties;
      } catch (e) {
        // console.log('crm properties error', e);
        return [];
      }
    }
    return [];
  }, [importedrows]);

  const checked = useMemo(() => {
    const checkedRows: {
      [key: string]: boolean;
    } = {};
    const phoneMap: {
      [key: string]: boolean;
    } = {};
    rows.forEach((row) => {
      checkedRows[row.property] = !!row?.selected;
      phoneMap[row.property] = !!(row?.phone || row?.defaultPhone);
    });
    return {
      selectedMap: checkedRows,
      phoneMap,
    };
  }, [rows]);

  useEffect(() => {
    setSelectedRows(checked.selectedMap);
    setPhoneRows(checked.phoneMap);
  }, [checked]);

  const selectedTouched = useMemo(() => {
    let selectTouched = false;
    let phoneTouched = false;
    Object.keys(selectedRows).forEach((key) => {
      if (selectedRows[key] !== checked.selectedMap[key]) {
        selectTouched = true;
      }
    });
    Object.keys(phoneRows).forEach((key) => {
      if (phoneRows[key] !== checked.phoneMap[key]) {
        phoneTouched = true;
      }
    });

    return {
      selectTouched,
      phoneTouched,
    };
  }, [selectedRows, phoneRows]);

  const [
    callTranscriptionSettingsCached,
    callTranscriptionSettingsLoadable,
  ] = makeApiRequestCached<ICallTranscriptionSettings>(
    {
      endpoint: getCallTranscriptionSettingsEndpoint(userAtom),
      method: 'get',
      stage: CALL_TRANSCRIPTION_SETTINGS_STAGE,
      domain: process.env.VITE_APP_CDK_DOMAIN,
    },
    (loadable, _cached) => {
      try {
        const cts = loadable.contents?.response?.data;
        if (cts) {
          return {
            minimumDuration: cts.minimumDuration ?? undefined,
            isCallStrengthKeywordsEnabled:
              cts.isCallStrengthKeywordsEnabled ?? false,
            isCallStrengthPhrasesEnabled:
              cts.isCallStrengthPhrasesEnabled ?? false,
            isCallStrengthCallSentimentEnabled:
              cts.isCallStrengthCallSentimentEnabled ?? false,
            phrases: cts.phrases ?? [],
            keywords: cts.keywords ?? [],
          };
        }
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
      return {
        minimumDuration: undefined,
        isCallStrengthKeywordsEnabled: false,
        isCallStrengthPhrasesEnabled: false,
        isCallStrengthCallSentimentEnabled: false,
        phrases: [],
        keywords: [],
      };
    }
  );

  const accountInitialValues = useMemo(
    () => ({
      id: userAtom.businessid ?? '',
      api: userAtom.apikey ?? '',
      crmProperties: {
        crm:
          CRMNameMap[
            getBusinessCRMPropertiesLoadable?.contents?.response?.data?.[0]
              ?.crmID ?? 0
          ] ?? 'none',
        lastUpdatedDate:
          getBusinessCRMPropertiesLoadable?.contents?.response?.data?.[0]
            ?.updatedDateTime ?? 'N/A',
        importedrows,
        rows,
        token:
          getBusinessCRMPropertiesLoadable?.contents?.response?.data?.[0]
            ?.token,
      },
      accountSettings: {
        CallRecord: businessData?.CallRecord === 1,
        sendRecordingToCRM: businessData?.sendRecordingToCRM === 1,
        createContact: businessData?.createContact === 1,
        hasWebhook: businessData?.hasWebhook === 1,
        onlyManagersCanViewCallReporting:
          businessData?.onlyManagersCanViewCallReporting === 1,
        showOutcomeForAllAgents: businessData?.showOutcomeForAllAgents === 1,
        requireOutcomeForAllAgents:
          businessData?.requireOutcomeForAllAgents === 1,
        allowAgentPowerlist: businessData?.allowAgentPowerlist === 1,
        onlyManagersCanAddPhoneNumbers:
          businessData?.onlyManagersCanAddPhoneNumbers === 1,
        blockOutboundCallRecordingSetting:
          businessData?.blockOutboundCallRecordingSetting ?? '',
        connectionDuration: businessData?.connectionDuration ?? '',
        logPowercallAttemptsToCrm:
          businessData?.logPowercallAttemptsToCrm === 1,
        canDeleteUsers: businessData?.canDeleteUsers === 1,
        autoAnswerApi: businessData?.autoAnswerApi === 1,
        apiKeyEnabled: businessData?.apikeyenabled === 1,
        businessDiscount: businessData?.discountPercent ?? '',
        discountEndDate: businessData?.discountEndDate
          ? businessData?.discountEndDate?.slice(0, 10)
          : null,
        businessBillingTerms: businessData?.businessBillingTerm ?? '',
        businessName: businessData?.name ?? '',
        businessContractTerms: businessData?.businessContractTerm ?? '',
        timezone: businessData?.timezone_php ?? 'America/Los_Angeles',
        DncBlocksOutboundSMS: businessData?.DncBlocksOutboundSMS === 1,
        stopBlocksOutboundCalls: businessData?.stopBlocksOutboundCalls === 1,
        // defaultCrmUserid: businessData?.defaultCrmUserid ?? '',
        usagePlanProduct: getCurrentUsagePlan(
          businessCached?.current?.businessDetails?.apiKeyJson
        ),
        machineDetectionTime:
          businessData?.machineDetectionTime ??
          DEFAULT_MACHINE_DETECTION_TIME_MS,
        allowRingOnHeld: !!businessData?.allowRingOnHeld,
        allowPauseRecording: businessData?.allowPauseRecording === 1,
      },
      localPresencePremiumActionPayload: getDefaultActionPayload2({
        defaultAction:
          businessCached.current?.businessDetails?.localPresencePremiumAction,
      }),
      defaultCrmUserid: businessData?.defaultCrmUserid ?? '',
      submitHoldMusicMp3File: {
        files: '',
        size: 0,
        name: businessData?.holdMusic ?? '',
      },
      recordNow: 'mp3',
      businessHours:
        businessCached.current?.businessDetails?.businessHours ??
        emptyHoursObject,
      customCRM: {
        crmName: crmDataMemo?.crmName ?? '',
        callsWebhook: crmDataMemo?.callsWebhook ?? '',
        hasCustomCRM: Boolean(crmDataMemo?.hasCustomCRM == '1'),
        smsWebhook: crmDataMemo?.smsWebhook ?? '',
        searchEndpoint: crmDataMemo?.searchEndpoint ?? '',
        contactUpdateEndpoint: crmDataMemo?.contactUpdateEndpoint ?? '',
        dispositionEndpoint: crmDataMemo?.dispositionEndpoint ?? '',
        activityEndpoint: crmDataMemo?.activityEndpoint ?? '',
        applyCustomCRMtoNewUsers: crmDataMemo?.applyCustomCRMtoNewUsers === 1,
        headers: crmDataMemo?.crmHeaders
          ? crmDataMemo.crmHeaders
          : [
              {
                name: '',
                value: '',
              },
            ],
      },
      businessHarassmentPolicies: businessCached.current
        ?.businessHarassmentPolicies?.length
        ? businessCached.current?.businessHarassmentPolicies?.map((policy) => {
            const luxonDuration = Duration.fromMillis(
              policy.durationMin * 60 * 1000 // 1 day
            )
              .shiftTo('days', 'hours', 'minutes', 'seconds', 'milliseconds')
              .toObject();
            return {
              ...policy,
              days: luxonDuration.days,
              hours: luxonDuration.hours,
              minutes: luxonDuration.minutes,
            } as BusinessHarassmentPolicy & {
              days: number;
              hours: number;
              minutes: number;
            };
          })
        : ([
            {
              channel: 'call',
              frequency: 3,
              durationMin: 60 * 24,
              days: 1,
              hours: 0,
              minutes: 0,
            },
            {
              channel: 'sms',
              frequency: 2,
              durationMin: 60 * 24,
              days: 1,
              hours: 0,
              minutes: 0,
            },
          ] as (BusinessHarassmentPolicy & {
            days: number;
            hours: number;
            minutes: number;
          })[]),
      callTranscriptionSettings: {
        isCallStrengthPhrasesEnabled:
          callTranscriptionSettingsLoadable?.contents?.response?.data
            ?.isCallStrengthPhrasesEnabled ?? false,
        isCallStrengthKeywordsEnabled:
          callTranscriptionSettingsLoadable?.contents?.response?.data
            ?.isCallStrengthKeywordsEnabled ?? false,
        isCallStrengthCallSentimentEnabled:
          callTranscriptionSettingsLoadable?.contents?.response?.data
            ?.isCallStrengthCallSentimentEnabled ?? false,
        minimumDuration:
          callTranscriptionSettingsLoadable?.contents?.response?.data
            ?.minimumDuration ?? CALL_TRANSCRIPTION_MIN_CALL_DURATION_MINUTES,
      },
      aioSettings: {
        intentScoreThreshold: businessData?.intentScoreThreshold ?? 0,
      },
    }),
    [
      userAtomLoadable,
      businessData,
      getBusinessCRMPropertiesLoadable,
      businessLoadable,
      crmDataMemo,
      usagePlansLoadable,
      callTranscriptionSettingsLoadable?.contents?.response?.data,
    ]
  );

  const isDeveloper = useMemo(
    () =>
      getIsDeveloper({
        userid: userAtom.userid,
        businessId: userAtom.businessid,
      }),
    [userAtom]
  );

  const usagePlansToDisplay = useMemo(() => {
    if (usagePlansCached?.current?.length) {
      if (canViewAllUsagePlans) {
        return usagePlansCached?.current; // Developer can view all usages plans
      }
      if (!usageGrandfathered) {
        // Businesses that are NOT on a “Grandfathered In” plan should not see ANY of the “Grandfathered In” plans in the drop down to select.
        return usagePlansCached?.current?.filter(
          (plan, i) => !plan?.product?.toLowerCase()?.includes('grandfather')
        );
      } else {
        // For businesses that are on the “Grandfathered In” ONLY show API Levels that are higher than the Grandfathered In Plan.  For example if the Grandfathered In plan is 10k only show API levels above 10k.
        return usagePlansCached?.current?.filter(
          (plan, i) => plan?.quotaLimit >= (currentUsagePlan?.quotaLimit ?? 0)
        );
      }
    } else {
      return [];
    }
  }, [
    usagePlansCached,
    usageGrandfathered,
    currentUsagePlan,
    canViewAllUsagePlans,
  ]);

  const dealClosedDate = businessData?.dealClosedDateTime?.slice?.(0, 10) ?? '';
  const dealClosedBy = useMemo(() => {
    if (businessData) {
      return businessData?.dealClosedBy;
    }
  }, [businessData?.dealClosedBy]);
  // used to store validated customCRM values
  const [validationOfCustomCRM, setValidationOfCustomCRM] = useState<
    ICustomCRM | undefined
  >();
  // sync from accountInitialValues
  useEffect(() => {
    setValidationOfCustomCRM(accountInitialValues.customCRM);
  }, [accountInitialValues.customCRM]);

  // used to store customCRM errors
  const [formikErrorsForCustomCRM, setFormikErrorsForCustomCRM] = useState<
    | {
        customCRM: TFormikErrorsForICustomCRM;
      }
    | Record<string, unknown>
  >({});

  const radioStyles = makeStyles({
    centerColumn: (props: { justify: string }) => ({
      display: 'flex',
      justifyContent: props.justify ?? '',
    }),
    green: {
      color: green[500],
    },
    blue: {
      color: blue[500],
    },
  });

  const RadioColumn = (props: {
    on: boolean;
    color: 'green' | 'blue';
    /** tooltip for icon */
    tooltip?: string | boolean | ReactChild;
    /** default: center */
    align?: 'center' | 'left' | 'right';
    disabled: boolean;
    clickHandler?: (e) => void;
  }) => {
    const classes = radioStyles({ justify: props.align ?? 'center' });
    const content = useMemo(
      () =>
        props.on ? (
          <RadioButtonCheckedIcon
            className={props.color === 'green' ? classes.green : classes.blue}
          />
        ) : (
          <RadioButtonUncheckedIcon />
        ),
      [props.on, props.color, classes]
    );
    return (
      <div className={classes.centerColumn}>
        {props.tooltip !== undefined ? (
          <Tooltip title={props.tooltip}>
            <span>
              <IconButton
                disabled={props.disabled}
                onClick={props.clickHandler}
                style={{ padding: '4px' }}
                size="large"
              >
                {content}
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          content
        )}
      </div>
    );
  };

  const partiallySelected = useMemo(() => {
    const totalRowCount = Object.keys(selectedRows ?? {})?.length ?? 0;
    const selectedCount =
      Object.keys(selectedRows ?? {}).filter((elemRow) => {
        return selectedRows[elemRow];
      })?.length ?? 0;
    if (totalRowCount > 0) {
      if (selectedCount > 0 && selectedCount < totalRowCount) {
        return true;
      }
      return false;
    } else {
      return false;
    }
  }, [selectedRows]);

  const allSelected = useMemo(() => {
    const totalRowCount = Object.keys(selectedRows ?? {})?.length ?? 0;
    const selectedCount =
      Object.keys(selectedRows ?? {}).filter((elemRow) => {
        return selectedRows[elemRow];
      })?.length ?? 0;
    if (totalRowCount > 0) {
      if (selectedCount === totalRowCount) {
        return true;
      }
      return false;
    } else {
      return false;
    }
  }, [selectedRows]);

  const onChangeAllHandler = () => {
    const updatedCheckedRows = {
      ...selectedRowsSearch,
    };
    if (partiallySelected) {
      Object.keys(updatedCheckedRows ?? {}).forEach((elemRow) => {
        (updatedCheckedRows ?? {})[elemRow] = false;
      });
    } else if (allSelected) {
      Object.keys(updatedCheckedRows ?? {}).forEach((elemRow) => {
        (updatedCheckedRows ?? {})[elemRow] = false;
      });
    } else {
      Object.keys(updatedCheckedRows ?? {}).forEach((elemRow) => {
        (updatedCheckedRows ?? {})[elemRow] = true;
      });
    }
    setSelectedRows({
      ...selectedRows,
      ...updatedCheckedRows,
    });
  };

  const getLabel = (elem: any): string => {
    const lastPos = elem?.property?.lastIndexOf('.');
    if (lastPos >= 0) {
      return elem?.property?.slice(lastPos + 1) || '';
    } else {
      return elem.label;
    }
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      hide: true,
      disableSearch: true, // do not search field content
      headerClassName: clsx(dataTableScss.fullWidth, classes.borderOutline2),
      cellClassName: clsx(dataTableScss.fullWidth, classes.borderOutline),
      enableSorting: false,
    },
    {
      field: 'selected',
      width: 50,
      enableSorting: false,
      disableSearch: true, // do not search field content
      headerClassName: clsx(classes.borderOutline2),
      cellClassName: clsx(classes.borderOutline),
      Header: () => {
        return (
          <div>
            <FormControlLabel
              label={''}
              style={{ marginBottom: '0px', marginTop: '0px', height: '28px' }}
              control={
                <Checkbox
                  className={classes.crmPropertyCheckbox}
                  onChange={onChangeAllHandler}
                  color="secondary"
                  indeterminate={partiallySelected}
                  checked={allSelected}
                />
              }
            />
          </div>
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        const onChangeHandler = () => {
          const updatedCheckedRows = {
            ...selectedRows,
            [params.row.property]: !selectedRows?.[params.row.property],
          };
          setSelectedRows(updatedCheckedRows);
        };
        return (
          <div>
            <FormControlLabel
              label={''}
              style={{ marginBottom: '0px', marginTop: '0px', height: '28px' }}
              control={
                <Checkbox
                  className={classes.crmPropertyCheckbox}
                  onChange={onChangeHandler}
                  color="secondary"
                  checked={selectedRows?.[params.row.property] ?? false}
                />
              }
            />
          </div>
        );
      },
    },
    // {
    //   field: 'crm',
    //   headerName: 'CRM',
    //   headerClassName: clsx(dataTableScss.fullWidth, classes.borderOutline2),
    //   cellClassName: clsx(dataTableScss.fullWidth, classes.borderOutline),
    //   valueFormatter: (params) =>
    //     params.value?.[0]?.toUpperCase() + params.value?.slice(1),
    //   //   hidden: true // hide field but still searchable
    // },
    {
      field: 'property',
      headerName: 'Property',
      headerClassName: clsx(dataTableScss.fullWidth, classes.borderOutline2),
      cellClassName: clsx(dataTableScss.fullWidth, classes.borderOutline),
    },
    {
      field: 'label',
      headerName: 'Label',
      headerClassName: clsx(dataTableScss.fullWidth, classes.borderOutline2),
      cellClassName: clsx(dataTableScss.fullWidth, classes.borderOutline),
      renderCell: (params: GridRenderCellParams) => {
        return <span>{getLabel(params.row)}</span>;
      },
    },
    {
      field: 'phone',
      width: 70,
      headerName: 'Phone',
      enableSorting: false,
      disableSearch: true, // do not search field content
      headerClassName: clsx(classes.borderOutline2),
      cellClassName: clsx(classes.borderOutline),
      renderCell: (params: GridRenderCellParams) => {
        const onChangeHandler = () => {
          const updatedCheckedRows = {
            ...phoneRows,
            [params.row.property]: !phoneRows?.[params.row.property],
          };
          setPhoneRows(updatedCheckedRows);
        };
        const isDefaultPhoneField = !!params.row.defaultPhone;
        return (
          <div>
            <FormControlLabel
              label={''}
              style={{ marginBottom: '0px', marginTop: '0px', height: '28px' }}
              control={
                <Checkbox
                  disabled={isDefaultPhoneField}
                  className={
                    isDefaultPhoneField
                      ? classes.crmPropertyCheckboxDisabled
                      : classes.crmPropertyCheckbox
                  }
                  onChange={onChangeHandler}
                  color="secondary"
                  checked={
                    !!(isDefaultPhoneField || phoneRows?.[params.row.property])
                  }
                />
              }
            />
          </div>
        );
      },
    },
  ] /* satisfies */ as IColDef[];

  const importCRMPropertiesHandler = async () => {
    try {
      setImportLoading(true);
      const data = {
        call: endpointEnum.BUSINESS_CRM,
        useLastSyncUser: true,
        userId: userAtom?.userid,
        businessId: userAtom?.businessid,
        crmId: CRMIDMap[formikRef.current?.values?.crmProperties?.crm ?? 0],
        selectedRows,
        phoneRows,
      };
      const response = await mutation.mutateAsync({
        endpoint: endpointEnum.BUSINESS_CRM,
        method: 'POST',
        payload: data,
        domain: process.env.VITE_APP_CDK_DOMAIN,
        stage: 'itn',
      });

      if (response?.data?.success) {
        if (response?.data?.data) {
          const crmName = CRMNameMap[response?.data?.data?.[0]?.crmID] ?? '';
          const importedObj = response?.data?.data?.[0]?.properties ?? {};
          let imported = Object.keys(importedObj).map((crmProperty) => {
            if (crmProperty) {
              return {
                ...importedObj[crmProperty],
              };
            }
          });
          imported = imported.filter((elem) => elem?.name);
          imported = imported.sort(
            (a, b) => (a?.displayOrder ?? -1) - (b?.displayOrder ?? -1)
          );
          formikRef?.current?.setFieldValue(
            'crmProperties.importedrows',
            imported
          );
          imported = imported.map((elem) => {
            return {
              id: elem.id,
              crm: crmName,
              property: elem.name,
              defaultPhone: elem?.defaultPhone ?? false,
              importdatetime: elem?.updatedDateTime ?? '',
              selected: elem?.selected ?? 0,
              phone: elem?.phone || elem?.defaultPhone ? 1 : 0,
              label: elem?.label ?? '',
            };
          });
          formikRef?.current?.setFieldValue('crmProperties.rows', imported);
          const checkedRows = {};
          imported.forEach((row) => {
            checkedRows[row.property] = !!row?.selected;
          });
          formikRef?.current?.setFieldValue(
            'crmProperties.lastUpdatedDate',
            response?.data?.data?.[0]?.updatedDateTime ??
              new Date().toISOString()
          );
          formikRef?.current?.setFieldValue(
            'crmProperties.token',
            response?.data?.data?.[0]?.token
          );
          setSelectedRows(checkedRows);
        }
        enqueueSnackbarWithStyles({
          message: `CRM contact properties has been successfully imported`,
          alertProps: { severity: 'success' },
        });
      } else {
        enqueueSnackbarWithStyles({
          message: `There was an error importing CRM contact properties`,
          alertProps: { severity: 'error' },
        });
        formikRef?.current?.setFieldValue('crmProperties.importedrows', []);
        formikRef?.current?.setFieldValue('crmProperties.rows', []);
        formikRef?.current?.setFieldValue('crmProperties.token', '');
        setSelectedRows({});
        formikRef?.current?.setFieldValue(
          'crmProperties.lastUpdatedDate',
          new Date().toISOString()
        );
      }
      setImportLoading(false);
      // setRefetchToggleForGetBusinessCRMProperties({});
    } catch (err) {
      enqueueSnackbarWithStyles({
        message: `There was an error importing CRM contact properties`,
        alertProps: { severity: 'error' },
      });
    }
  };

  const activeCRMName =
    formikRef.current?.values?.crmProperties?.crm !== 'none'
      ? (formikRef.current?.values?.crmProperties?.crm?.[0]?.toUpperCase() ??
          '') + (formikRef.current?.values?.crmProperties?.crm?.slice(1) ?? '')
      : '';

  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        className={classes.searchBar}
        dataType="CRM Properties"
      />
    ),
    [activeCRMName]
  );

  const businessUsersLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    })
  );

  const hasAnyAgentWithPowerDialerMachineDetectionProduct: boolean = useMemo(() => {
    return (businessUsersLoadable.contents?.response?.data?.result ?? []).some(
      (user: any) =>
        user?.powerlistProductId == POWERDIALER_MACHINE_DETECT_PRODUCT_ID
    );
  }, [businessUsersLoadable]);

  const isMachineDetectionSectionVisible =
    isMachineDetectionFeatureEnabled &&
    hasAnyAgentWithPowerDialerMachineDetectionProduct;

  const callTranscriptionSettings = useMemo<ICallTranscriptionSettings>(() => {
    const cts = callTranscriptionSettingsCached?.current;
    return {
      minimumDuration: cts.minimumDuration ?? undefined,
      isCallStrengthKeywordsEnabled: cts.isCallStrengthKeywordsEnabled ?? false,
      isCallStrengthPhrasesEnabled: cts.isCallStrengthPhrasesEnabled ?? false,
      isCallStrengthCallSentimentEnabled:
        cts.isCallStrengthCallSentimentEnabled ?? false,
      phrases: cts.phrases ?? [],
      keywords: cts.keywords ?? [],
    };
  }, [callTranscriptionSettingsLoadable.state]);

  const [minimumDuration, setMinimumDuration] = useState<number | undefined>(
    undefined
  );

  const updateMinimumDuration = (e) => {
    const integer = /^[1-9\b][0-9\b]*$/;
    if (e.target.value === '' || integer.test(e.target.value)) {
      setMinimumDuration(Number.parseInt(e.target.value));
    } else {
      setMinimumDuration(undefined);
    }
  };

  const [phrases, setPhrases] = useState<Record<string, IEditableText>>({});

  const addPhrase = () => {
    const newId = uuidv4();
    const updatedPhrases = {
      ...phrases,
      [newId]: {
        id: newId,
        text: '',
        isEditing: true,
        touched: false,
      },
    };
    setPhrases(updatedPhrases);
    return updatedPhrases;
  };

  const editPhrase = (id) => {
    const phrase = phrases[id];
    const updatedPhrases = {
      ...phrases,
      [id]: {
        ...phrase,
        isEditing: !phrase.isEditing,
      },
    };
    setPhrases(updatedPhrases);
    return updatedPhrases;
  };

  const updatePhrase = (e, id) => {
    const phrase = phrases[id];
    const updatedPhrases = {
      ...phrases,
      [id]: {
        ...phrase,
        text: e.target.value,
        touched: true,
      },
    };
    setPhrases(updatedPhrases);
    return updatedPhrases;
  };

  const deletePhrase = (id) => {
    const { [id]: _, ...remainingPhrases } = phrases;
    setPhrases(remainingPhrases);
    return remainingPhrases;
  };

  const [keywords, setKeywords] = useState<Record<string, IEditableText>>({});

  const addKeyword = () => {
    const newId = uuidv4();
    const updatedKeywords = {
      ...keywords,
      [newId]: {
        id: newId,
        text: '',
        isEditing: true,
        touched: false,
      },
    };
    setKeywords(updatedKeywords);
    return updatedKeywords;
  };

  const editKeyword = (id) => {
    const keyword = keywords[id];
    const updatedKeywords = {
      ...keywords,
      [id]: {
        ...keyword,
        isEditing: !keyword.isEditing,
      },
    };
    setKeywords(updatedKeywords);
    return updatedKeywords;
  };

  const updateKeyword = (e, id) => {
    const keyword = keywords[id];
    const updatedKeywords = {
      ...keywords,
      [id]: {
        ...keyword,
        text: e.target.value,
        touched: true,
      },
    };
    setKeywords(updatedKeywords);
    return updatedKeywords;
  };

  const deleteKeyword = (id) => {
    const { [id]: _, ...remainingKeywords } = keywords;
    setKeywords(remainingKeywords);
    return remainingKeywords;
  };

  const [
    isCallStrengthKeywordsEnabled,
    setIsCallStrengthKeywordsEnabled,
  ] = useState(false);

  const toggleCallStrengthKeywordsEnabled = () => {
    setIsCallStrengthKeywordsEnabled(!isCallStrengthKeywordsEnabled);
  };

  const [
    isCallStrengthPhrasesEnabled,
    setIsCallStrengthPhrasesEnabled,
  ] = useState(false);

  const toggleCallStrengthPhrasesEnabled = () => {
    setIsCallStrengthPhrasesEnabled(!isCallStrengthPhrasesEnabled);
  };

  const [
    isCallStrengthCallSentimentEnabled,
    setIsCallStrengthCallSentimentEnabled,
  ] = useState(false);

  const toggleCallStrengthCallSentimentEnabled = () => {
    setIsCallStrengthCallSentimentEnabled(!isCallStrengthCallSentimentEnabled);
  };

  const [
    hasInitializedCallTranscriptionSettings,
    setHasInitializedCallTranscriptionSettings,
  ] = useState(false);

  useEffect(() => {
    if (
      hasInitializedCallTranscriptionSettings ||
      callTranscriptionSettingsLoadable.state !== 'hasValue'
    ) {
      return;
    }

    setHasInitializedCallTranscriptionSettings(true);

    setMinimumDuration(callTranscriptionSettings.minimumDuration);

    setIsCallStrengthKeywordsEnabled(
      callTranscriptionSettings.isCallStrengthKeywordsEnabled
    );

    setIsCallStrengthPhrasesEnabled(
      callTranscriptionSettings.isCallStrengthPhrasesEnabled
    );

    setIsCallStrengthCallSentimentEnabled(
      callTranscriptionSettings.isCallStrengthCallSentimentEnabled
    );

    setPhrases(
      callTranscriptionSettings.phrases.reduce((phrases, phrase) => {
        const newId = uuidv4();
        phrases[newId] = {
          id: newId,
          text: phrase,
          isEditing: false,
        };
        return phrases;
      }, {})
    );

    setKeywords(
      callTranscriptionSettings.keywords.reduce((keywords, keyword) => {
        const newId = uuidv4();
        keywords[newId] = {
          id: newId,
          text: keyword,
          isEditing: false,
        };
        return keywords;
      }, {})
    );
  }, [
    callTranscriptionSettingsLoadable,
    hasInitializedCallTranscriptionSettings,
  ]);

  const isValidText = (maxChars: number, text?: string) => {
    const chars = text?.length ?? 0;
    return chars > 0 && chars <= maxChars;
  };

  const isValidPhrase = (text) =>
    isValidText(CALL_TRANSCRIPTION_MAX_PHRASE_CHARACTERS, text);

  const isValidKeyword = (text) =>
    isValidText(CALL_TRANSCRIPTION_MAX_KEYWORD_CHARACTERS, text);

  const isValidIsEditing = (isEditing) => {
    if (typeof isEditing === 'boolean' && isEditing) {
      return false;
    }
    return true;
  };
  function trackUpdates(values, accountInitialValues, currentTabTitle) {
    trackModalUpdates(
      values,
      accountInitialValues,
      'Account Settings Updated',
      currentTabTitle
    );
  }
  const callTranscriptionValidationSchema = Yup.object({
    callTranscriptionSettings: Yup.object({
      minimumDuration: Yup.number().test(
        'minimumDuration',
        `Must be between ${CALL_TRANSCRIPTION_MIN_CALL_DURATION_MINUTES} and ${CALL_TRANSCRIPTION_MAX_CALL_DURATION_MINUTES}`,
        (minimumDuration) => {
          return (
            typeof minimumDuration === 'undefined' ||
            (typeof minimumDuration === 'number' &&
              minimumDuration >= CALL_TRANSCRIPTION_MIN_CALL_DURATION_MINUTES &&
              minimumDuration <= CALL_TRANSCRIPTION_MAX_CALL_DURATION_MINUTES)
          );
        }
      ),
      isCallStrengthKeywordsEnabled: Yup.boolean(),
      isCallStrengthPhrasesEnabled: Yup.boolean(),
      isCallStrengthCallSentimentEnabled: Yup.boolean(),
      phrases: Yup.lazy((phrases: any) =>
        Yup.object().shape(
          Object.keys(phrases ?? {}).reduce(
            (acc, phrase) => ({
              ...acc,
              [phrase]: Yup.object({
                text: Yup.string().test(
                  'phrase',
                  'Invalid Phrase',
                  isValidPhrase
                ),
                isEditing: Yup.bool().test(
                  'isEditing',
                  'Is Editing',
                  isValidIsEditing
                ),
              }),
            }),
            {}
          )
        )
      ),
      keywords: Yup.lazy((keywords: any) =>
        Yup.object().shape(
          Object.keys(keywords ?? {}).reduce(
            (acc, keyword) => ({
              ...acc,
              [keyword]: Yup.object({
                text: Yup.string().test(
                  'keyword',
                  'Invalid Keyword',
                  isValidKeyword
                ),
                isEditing: Yup.bool().test(
                  'isEditing',
                  'Is Editing',
                  isValidIsEditing
                ),
              }),
            }),
            {}
          )
        )
      ),
    }),
  });

  const isCallTranscriptionSettingsTab =
    isCallTranscriptionsFeaturedEnabled &&
    tabIndex.toString() === CALL_TRANSCRIPTION_SETTINGS_TAB_INDEX;

  return (
    <Paper elevation={0} square={true}>
      <Formik
        innerRef={formikRef}
        validationSchema={
          isCallTranscriptionSettingsTab
            ? callTranscriptionValidationSchema
            : role === Role.SUPERADMIN
            ? validationSchemaForSuperAdmin
            : validationSchema
        }
        validate={async (values) => {
          // validate custom crm links if any values changed occurred
          try {
            if (!(_ as any).isEqual(validationOfCustomCRM, values.customCRM)) {
              setValidationOfCustomCRM(values.customCRM);
              const webhookTestResults = await Promise.all(
                webhooksComponent(values.customCRM)
                  .filter((component) => component.endpoint) // filter out empty string
                  .map(async (component) => {
                    try {
                      await mutation.mutateAsync({
                        endpoint: `${
                          endpointEnum.FORWARDER
                        }?url=${encodeURIComponent(component.endpoint)}`,
                      });
                      return {
                        name: component.name,
                        sucess: true,
                      };
                    } catch (exception) {
                      return {
                        name: component.name,
                        sucess: false,
                      };
                    }
                  })
              );
              const customCrmErrors = webhookTestResults
                .filter((test) => !test.sucess)
                .reduce(
                  (acc, test) => ({
                    ...acc,
                    [test.name]:
                      'Unable to validate webhook (url link returns an error)',
                  }),
                  {}
                );
              if (Object.keys(customCrmErrors).length) {
                setFormikErrorsForCustomCRM({
                  customCRM: customCrmErrors,
                });
                return { customCRM: customCrmErrors };
              } else {
                setFormikErrorsForCustomCRM({});
                return {};
              }
            }
            return formikErrorsForCustomCRM;
          } catch (e) {
            // console.log('validate error', e);
          }
        }}
        onSubmit={async (values, actions) => {
          trackUpdates(values, accountInitialValues, currentTabTitle);

          if (isCallTranscriptionSettingsTab) {
            const response = await mutation.mutateAsync({
              endpoint: getCallTranscriptionSettingsEndpoint(userAtom),
              method: 'put',
              stage: CALL_TRANSCRIPTION_SETTINGS_STAGE,
              domain: process.env.VITE_APP_CDK_DOMAIN,
              payload: {
                minimumDuration: minimumDuration ?? MINIMUM_DURATION_MINS,
                isCallStrengthKeywordsEnabled,
                isCallStrengthPhrasesEnabled,
                isCallStrengthCallSentimentEnabled,
                phrases: Object.values(phrases ?? {})
                  .map((phrase) => phrase.text.trim())
                  .filter(Boolean),
                keywords: Object.values(keywords ?? {})
                  .map((keyword) => keyword.text.trim())
                  .filter(Boolean),
              },
            });

            if (response.status === 200) {
              enqueueSnackbarWithStyles({
                message:
                  'Successfully Updated Conversation Intelligence Settings',
                alertProps: { severity: 'success' },
              });

              setRefetchToggleForCallTranscriptionSettings({});

              props.onHide();
            } else {
              enqueueSnackbarWithStyles({
                message:
                  'Encountered an error updating Conversation Intelligence Settings',
                alertProps: { severity: 'warning' },
              });
            }

            return;
          }

          const customCRMChanged =
            JSON.stringify(accountInitialValues?.customCRM) !==
            JSON.stringify(values.customCRM);
          const businessHoursChanged =
            JSON.stringify(accountInitialValues.businessHours) !==
            JSON.stringify(values.businessHours);
          const businessHarassmentPoliciesChanged =
            JSON.stringify(accountInitialValues?.businessHarassmentPolicies) !==
            JSON.stringify(values.businessHarassmentPolicies);
          const arr: {
            [key: string]: any;
          } = {};
          const filteredHeaders = values?.customCRM?.headers.filter(
            (header) => header.name !== '' && header.value !== ''
          );
          arr.defaultCrmUserId = values.defaultCrmUserid;
          const localPresencePremiumAction = getDefaultActionUrl({
            ...values.localPresencePremiumActionPayload,
            businessid: userAtom?.businessid,
            parentType: 'user',
            voicemail: String(userAtom?.userid),
          });
          if (
            localPresencePremiumAction &&
            businessCached.current?.businessDetails
              ?.localPresencePremiumAction !== localPresencePremiumAction
          ) {
            arr.localPresencePremiumAction = localPresencePremiumAction;
          } else {
            arr.localPresencePremiumAction = 'null';
          }
          if (customCRMChanged) {
            arr.customCRMChanged = '1';
          } else {
            arr.customCRMChanged = 'null';
          }
          arr.smsWebhook = values.customCRM?.smsWebhook ?? '';
          arr.callsWebhook = values.customCRM?.callsWebhook ?? '';
          arr.searchEndpoint = values.customCRM?.searchEndpoint ?? '';
          arr.userid = userAtom?.userid ?? 0;
          arr.crmName = values.customCRM?.crmName ?? '';
          arr.applyCustomCRMtoNewUsers =
            values.customCRM?.applyCustomCRMtoNewUsers ?? '';
          arr.dispositionEndpoint = values.customCRM?.dispositionEndpoint ?? '';
          arr.activityEndpoint = values.customCRM?.activityEndpoint ?? '';
          arr.contactUpdateEndpoint =
            values.customCRM?.contactUpdateEndpoint ?? '';
          arr.hasCustomCRM = values.customCRM?.hasCustomCRM === true ? 1 : 0;
          if (isAIOEnabled) {
            arr.intentScoreThreshold = values.aioSettings.intentScoreThreshold;
          }
          for (const name in values?.accountSettings ?? {}) {
            arr[name] =
              typeof values?.accountSettings[name] === 'boolean'
                ? values.accountSettings[name]
                  ? '1'
                  : '0'
                : values.accountSettings[name];
          }
          if (filteredHeaders?.length) {
            arr.headers = filteredHeaders;
          }
          if (
            values?.accountSettings?.apiKeyEnabled &&
            values?.accountSettings?.usagePlanProduct !== ''
          ) {
            arr.usagePlanProduct = values?.accountSettings?.usagePlanProduct;
          } else {
            arr.usagePlanProduct = null;
          }
          if (
            isMachineDetectionSectionVisible &&
            !!values?.accountSettings?.machineDetectionTime
          ) {
            arr.machineDetectionTime =
              values?.accountSettings?.machineDetectionTime;
          }
          const updatedProperties = values?.crmProperties?.importedrows ?? {};
          Object.keys(updatedProperties ?? {}).forEach((elem) => {
            updatedProperties[elem] = {
              ...updatedProperties[elem],
              selected: selectedRows?.[updatedProperties[elem].name] ? 1 : 0,
              phone:
                phoneRows?.[updatedProperties[elem].name] ||
                updatedProperties[elem].defaultPhone
                  ? 1
                  : 0,
            };
          });
          const importData = {
            businessId: userAtom?.businessid,
            userId: userAtom?.userid,
            crmId: CRMIDMap[values?.crmProperties?.crm],
            token: values?.crmProperties?.token,
            updatedDateTime: moment(
              values?.crmProperties?.lastUpdatedDate ?? ''
            ).format(),
            properties: JSON.stringify(updatedProperties),
            phoneFieldUpdate: selectedTouched.phoneTouched,
          };
          const formData = new FormData();
          if (uploadedFiles.holdMusic) {
            formData.append('call', 'submitHoldMusicMp3File');
            formData.append('mp3', uploadedFiles.holdMusic[0]);
            formData.append('businessid', String(userAtom.businessid));
            formData.append('apikey', userAtom.apikey);
          }
          const [
            updateBusiness2,
            uploadFiles,
            statusPatchBusiness,
            updateCRMProperties,
          ] = await Promise.all<any>([
            mutation.mutateAsync({
              endpoint: endpointEnum.UPDATE_BUSINESS_4,
              payload: {
                call: methodNameEnum.UPDATE_BUSINESS_4,
                apikey: userAtom?.apikey,
                businessid: userAtom?.businessid,
                name: values.accountSettings.businessName,
                timezone: values.accountSettings.timezone,
                arr: JSON.stringify(arr),
              },
            }),
            // reload businessData
            uploadedFiles.holdMusic
              ? Axios.post(
                  `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`,
                  formData,
                  { withCredentials: true }
                )
              : Promise.resolve({
                  status: 200,
                }),
            businessHoursChanged || businessHarassmentPoliciesChanged
              ? mutation.mutateAsync({
                  endpoint:
                    endpointEnum.PATCH_BUSINESSES + `/${userAtom.businessid}`,
                  method: 'patch',
                  omitBusinessIdAndApikey: true,
                  payload: {
                    businessDetails: {
                      businessHours: values.businessHours,
                    },
                    businessHarassmentPolicies: values.businessHarassmentPolicies.map(
                      (policy) => ({
                        ...policy,
                        durationMin:
                          Duration.fromObject({
                            days: policy.days,
                            hours: policy.hours,
                            minutes: policy.minutes,
                          })
                            .normalize()
                            .toMillis() /
                          60 /
                          1000,
                      })
                    ),
                  },
                })
              : Promise.resolve({
                  status: 200,
                }),
            isCrmPropertiesFeatureEnabled
              ? mutation.mutateAsync({
                  endpoint: endpointEnum.BUSINESS_CRM,
                  method: 'PUT',
                  payload: importData,
                  domain: process.env.VITE_APP_CDK_DOMAIN,
                  stage: 'itn',
                })
              : Promise.resolve({
                  status: 200,
                }),
          ]);
          if (statusPatchBusiness?.status !== 200) {
            enqueueSnackbarWithStyles({
              message: 'Encountered an error updating business hours',
              alertProps: { severity: 'warning' },
            });
          }
          if (updateBusiness2?.status !== 200) {
            enqueueSnackbarWithStyles({
              message: 'Encountered an error updating account settings',
              alertProps: { severity: 'warning' },
            });
          } else {
            refetchBusinessData();
            enqueueSnackbarWithStyles({
              message: 'Successfully Updated Settings',
              alertProps: { severity: 'success' },
            });
          }
          if (uploadFiles?.status !== 200) {
            enqueueSnackbarWithStyles({
              message: 'Encountered an error uploading mp3 files',
              alertProps: { severity: 'warning' },
            });
          }
          if (updateCRMProperties?.status !== 200) {
            enqueueSnackbarWithStyles({
              message: 'Encountered an error updating CRM properties',
              alertProps: { severity: 'warning' },
            });
          }
          if (updateCRMProperties?.data?.syncStatus === 400) {
            enqueueSnackbarWithStyles({
              message:
                'We couldn’t save your changes because we are still processing your current CRM Properties Sync. Please wait a few moments and try again.',
              alertProps: { severity: 'warning' },
            });
          }
          setRefetchToggleForBusinessData({});
          setRefetchToggleForBusinessDataV1({});
          setRefetchToggleForCustomCRM({});
          setRefetchToggleForGetBusinessCRMProperties({});
          refetchBusinessData();
          props.setUsagePlan?.(arr.usagePlanProduct);
          props.onHide();
        }}
        enableReinitialize={true}
        initialValues={accountInitialValues}
      >
        {(formik) => {
          function handleSubmitBusinessHours() {
            formik.setFieldValue(
              'businessHours',
              schedulableHoursCached.current
            );
            onHideBusinessHours();
          }
          const hours = formik.values.businessHours;
          useEffect(() => {
            setHoursString(createHoursString(hours));
          }, [hours]);
          const lastUpdatedDate =
            !!formik?.values?.crmProperties?.lastUpdatedDate &&
            formik?.values?.crmProperties?.lastUpdatedDate !== 'N/A'
              ? moment(formik?.values?.crmProperties?.lastUpdatedDate).format(
                  'MM/DD/YYYY h:mm a'
                )
              : 'N/A';

          const currentTabIndex =
            tabIndex >= tabNames.length ? tabNames.length - 1 : tabIndex;

          return (
            <>
              <TabContext value={String(currentTabIndex)}>
                <WizardModal
                  title={tabNames[tabIndex]?.title ?? 0}
                  isLoading={
                    formik.isSubmitting ||
                    isFetchingBusinessData ||
                    customCrmLoadable.state === 'loading' ||
                    callTranscriptionSettingsLoadable.state === 'loading' ||
                    !role
                  }
                  navigation={
                    <>
                      <Tab
                        label="Manage Account Settings"
                        disabled={true}
                        className={clsx(classes.tab)}
                        data-qa="manage-account-settings"
                      />
                      <TabList
                        onChange={handleChange(formik)}
                        aria-label="Account Settings"
                        orientation="vertical"
                        variant="standard"
                        className={clsx(classes.tabs, {})}
                      >
                        {tabNames.map((tabItem, index) => (
                          <Tab
                            {...{ name: tabItem.title }} // name not found in Tab
                            key={index}
                            className={classes.tab}
                            label={
                              <TabLabel
                                index={index}
                                formik={formik}
                                tabToFields={tabToFields}
                                tabItem={tabItem}
                              />
                            }
                            value={String(index)}
                          />
                        ))}
                      </TabList>
                    </>
                  }
                  dialogActions={
                    <>
                      <Button
                        data-qa="account-settings-cancel-button"
                        className={classes.button}
                        variant="contained"
                        disabled={formik.isSubmitting}
                        onClick={() => {
                          props.onHide();
                        }}
                        color="inherit"
                      >
                        Cancel
                      </Button>
                      {isCallTranscriptionSettingsTab ? (
                        <Button
                          data-qa="account-settings-save2-button"
                          className={classes.button}
                          variant="contained"
                          color="primary"
                          disabled={
                            formik.isSubmitting ||
                            !formik.dirty ||
                            !formik.isValid
                          }
                          onClick={async () => {
                            await formik.submitForm();
                          }}
                        >
                          Save
                        </Button>
                      ) : (
                        <Button
                          data-qa="account-settings-save-button"
                          className={classes.button}
                          variant="contained"
                          color="primary"
                          disabled={
                            // formik.isSubmitting || !formik.dirty || fileSizeError
                            formik.isSubmitting ||
                            (!formik.dirty &&
                              !(
                                selectedTouched.phoneTouched ||
                                selectedTouched.selectTouched
                              )) ||
                            importLoading ||
                            (showDiscountEndDate &&
                              !formik.values.accountSettings.discountEndDate)
                          }
                          onClick={(event) => {
                            // // if async function then save event since it will be garbage collected after any await
                            // const savedEvent = { ...event };

                            // trigger show errors if errors exists
                            formik.handleSubmit(event as any);

                            // switch tabs and suppress submit if error not in current tab (submit will block if errors detected)
                            if (
                              !formik.isValid && // if form invalid
                              !isErrorInTab(
                                tabIndex,
                                formik.errors,
                                tabToFields
                              ) // and error not in this tab
                            ) {
                              const errorTabIndex = getErrorTabIndex(
                                formik.errors,
                                tabToFields
                              );
                              /**
                               * switch to tab containing error
                               *
                               * since form validation is in flight and makes page rendering updates
                               * set state change in separate event loop cycle (tick) since it also makes page rendering updates
                               * putting state changes in a separate tick will prevent coliding page rendering updates
                               *
                               * @see https://blog.sessionstack.com/how-javascript-works-event-loop-and-the-rise-of-async-programming-5-ways-to-better-coding-with-2f077c4438b5
                               */
                              setTimeout(() => setTabIndex(errorTabIndex ?? 0));
                            }
                          }}
                        >
                          Save
                        </Button>
                      )}
                    </>
                  }
                  size="lg"
                  show={props.show}
                  onHide={() => {
                    props.onHide();
                  }}
                  onSubmit={() => {
                    formik.handleSubmit();
                  }}
                >
                  <TabPanel value="0">
                    {/* Settings */}
                    <RecordNow
                      dropdownLabel="Hold Music"
                      dropdownName="recordNow"
                      dropdownType="mp3"
                      outlineLabel="Hold Music"
                      filenameName="submitHoldMusicMp3File.files"
                      filesizeName="submitHoldMusicMp3File.name"
                      formik={formik}
                      filename={filenames.holdMusic}
                      filepath={filepaths.holdMusic}
                      onChangeFile={onChangeFile('holdMusic')}
                      onRecording={handleRecording('holdMusic')}
                      fileSizeError={fileSizeError}
                    >
                      <MenuItem data-qa="upload-hold-music-mp3" value="mp3">
                        MP3
                      </MenuItem>
                    </RecordNow>

                    <FormikTextField
                      name="accountSettings.businessName"
                      className={classes.field}
                      formik={formik}
                      fullWidth
                      label="Business Name"
                      size="small"
                      type="text"
                      required
                      variant="outlined"
                      data-qa="business-name-account-settings"
                    />
                    <MaterialAutocomplete
                      name="accountSettings.timezone"
                      data-qa="business-time-zone"
                      className={classes.field}
                      formik={formik}
                      type="text"
                      label="Business Timezone"
                      size="small"
                      variant="outlined"
                      options={timezonesDropdown}
                      required
                      filterOptions={filterOptions}
                      renderOption={(props, option) => (
                        <li {...props} style={{ width: '100%' }}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <span>
                              <span
                                style={{
                                  display: 'inline-block',
                                  width: 200,
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                }}
                              >
                                {timezones[option]?.shortName}
                              </span>
                              {option}
                            </span>
                            <span>{timezones[option]?.utcOffsetStr}</span>
                          </div>
                        </li>
                      )}
                    />
                  </TabPanel>
                  <TabPanel value="1">
                    {/* Call Options */}
                    <FormGroup>
                      <div className="row borderBottom">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">Record Calls</h5>
                          <p className="fieldDescription">
                            Record call audio to the Kixie dashboard. View and
                            playback recordings via the Play button on the Call
                            History page in the Actions column.
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <FormControlLabel
                            data-qa="record-calls"
                            control={
                              <FormikSwitch
                                name="accountSettings.CallRecord"
                                formik={formik}
                                color="primary"
                                type="checkbox"
                              />
                            }
                            label="Record Calls"
                          />
                        </div>
                        {formik.values.accountSettings.CallRecord && (
                          <>
                            <div className="subField">
                              <div className="col-lg-8">
                                <h5 className="fieldTitle">
                                  Send Recordings To CRM
                                </h5>
                                <p className="fieldDescription">
                                  Send recorded call audio to call logs synced
                                  to your connected CRM.
                                </p>
                              </div>
                              <div className="col-lg-4 px-0">
                                <FormControlLabel
                                  control={
                                    <FormikSwitch
                                      name="accountSettings.sendRecordingToCRM"
                                      data-qa="send-recordings-to-crm"
                                      formik={formik}
                                      color="primary"
                                      type="checkbox"
                                    />
                                  }
                                  label="Send Recordings To CRM"
                                />
                              </div>
                            </div>
                            <div className="subField">
                              <div className="col-lg-8">
                                <h5 className="fieldTitle">
                                  Outbound Recording
                                </h5>
                                <p className="fieldDescription">
                                  Select an option for recording call audio on
                                  outbound calls to states that require two
                                  party consent. All records all calls; Two
                                  Party States does not record calls to states
                                  requiring two party consent; None does not
                                  record any outbound calls.
                                </p>
                              </div>
                              <div className="col-lg-4 pl-0">
                                <FormikSelect
                                  name="accountSettings.blockOutboundCallRecordingSetting"
                                  data-qa="outbound-recording-selectbox"
                                  className="w-100"
                                  formik={formik}
                                  label="Outbound Recording"
                                  size="small"
                                  variant="outlined"
                                >
                                  <MenuItem
                                    data-qa="outbound-recording-selectbox-none"
                                    value="none"
                                  >
                                    All
                                  </MenuItem>
                                  <MenuItem
                                    data-qa="outbound-recording-selectbox-all"
                                    value="all"
                                  >
                                    None
                                  </MenuItem>
                                  <MenuItem
                                    data-qa="outbound-recording-selectbox-twoparty"
                                    value="twoparty"
                                  >
                                    Two Party
                                  </MenuItem>
                                </FormikSelect>
                              </div>
                            </div>
                            <div className="subField">
                              <div className="col-lg-8">
                                <h5 className="fieldTitle">
                                  Allow Pause Recording
                                </h5>
                                <p className="fieldDescription">
                                  Allow users to pause the call recording in
                                  Powercall
                                </p>
                              </div>
                              <div className="col-lg-4 px-0">
                                <FormControlLabel
                                  control={
                                    <FormikSwitch
                                      name="accountSettings.allowPauseRecording"
                                      data-qa="pause-call-recording-toggle"
                                      formik={formik}
                                      color="primary"
                                      type="checkbox"
                                    />
                                  }
                                  label="Pause Call Recording"
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="row borderBottom">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">Create Contacts</h5>
                          <p className="fieldDescription">
                            Create new contacts for phone calls to and from
                            numbers not already present in your connected CRM.
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <FormControlLabel
                            control={
                              <FormikSwitch
                                name="accountSettings.createContact"
                                data-qa="create-new-contacts"
                                formik={formik}
                                color="primary"
                                type="checkbox"
                              />
                            }
                            label="Create Contacts"
                          />
                        </div>
                      </div>
                      <div
                        className={`row ${
                          role === Role.SUPERADMIN ? 'borderBottom' : ''
                        }`}
                      >
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">
                            Connection Duration (seconds)
                          </h5>
                          <p className="fieldDescription">
                            Set the length, in seconds, for a call to be
                            considered Connected for reporting purposes. Set
                            this value to the minimum duration you expect agents
                            to be on a call for it be a meaningful connection.
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <FormikTextField
                            name="accountSettings.connectionDuration"
                            data-qa="connection-duration-seconds"
                            formik={formik}
                            style={{
                              marginRight: '8px',
                              flexGrow: 1,
                              width: '100%',
                            }}
                            label="Connection Duration (seconds)"
                            size="small"
                            type="number"
                            InputProps={{
                              inputProps: {
                                min: 0,
                                max: 1000,
                              },
                            }}
                            variant="outlined"
                          />
                        </div>
                      </div>
                      {role === Role.SUPERADMIN && (
                        <div className="row superStyle borderBottom">
                          <div className="col-lg-8">
                            <h5 className="fieldTitle">
                              Log PowerDial Attempts to CRM
                            </h5>
                            <p className="fieldDescription">
                              To avoid clutter, Kixie omits Dial Attempts that
                              did not connect to a Live Answer or Voicemail
                              Greeting from being sent to your CRM. If you want
                              to log those Dial Attempts in the CRM, toggle this
                              ON.
                            </p>
                          </div>
                          <div className="col-lg-4">
                            <FormControlLabel
                              control={
                                <FormikSwitch
                                  name="accountSettings.logPowercallAttemptsToCrm"
                                  data-qa="log-powerdial-attempts-to-crm"
                                  formik={formik}
                                  color="primary"
                                  type="checkbox"
                                />
                              }
                              label="Log PowerDial Attempts to CRM"
                            />
                          </div>
                        </div>
                      )}

                      <div className="row" style={{ width: '102.5%' }}>
                        {process.env.VITE_APP_FEATURE_OUTCOME === '1' && (
                          <LabelledOutline label="Call Outcomes">
                            <div className="row">
                              <div className="col-lg-8">
                                <h5 className="fieldTitle">
                                  Show Outcome for All Agents
                                </h5>
                                <p className="fieldDescription">
                                  When ON, the Powercall dialer will
                                  automatically display the Outcome Tab when a
                                  call has ended, allowing agents to quickly add
                                  an Outcome at the end of each call
                                </p>
                              </div>
                              <div className="col-lg-4">
                                <FormControlLabel
                                  control={
                                    <FormikSwitch
                                      name="accountSettings.showOutcomeForAllAgents"
                                      data-qa="show-negative-outcomes-for-all-agents"
                                      formik={formik}
                                      color="primary"
                                      type="checkbox"
                                      disabled={
                                        formik.values.accountSettings
                                          .requireOutcomeForAllAgents === true
                                      }
                                      handleChange={() => {
                                        setShowOutcomeForAllAgentsVal(
                                          !formik.values.accountSettings
                                            .showOutcomeForAllAgents
                                        );
                                        formik.setFieldValue(
                                          'accountSettings.showOutcomeForAllAgents',
                                          !formik.values.accountSettings
                                            .showOutcomeForAllAgents
                                        );
                                      }}
                                    />
                                  }
                                  label="Show Outcome for All Agents"
                                />
                              </div>
                            </div>
                            <div className="row mt-2">
                              <div className="col-lg-8">
                                <h5 className="fieldTitle">
                                  Require Outcome for All Agents
                                </h5>
                                <p className="mb-0 fieldDescription">
                                  When ON, agents must submit an Outcome after
                                  each call before placing the next one. Agents
                                  are automatically set to DND until an Outcome
                                  is logged.
                                  <br />
                                  This setting does not apply to PowerList
                                  calls, as requiring an Outcome can be
                                  configured directly within the PowerList
                                  settings.
                                </p>
                              </div>
                              <div className="col-lg-4">
                                <FormControlLabel
                                  control={
                                    <FormikSwitch
                                      name="accountSettings.requireOutcomeForAllAgents"
                                      data-qa="require-outcome-for-all-agents"
                                      formik={formik}
                                      color="primary"
                                      type="checkbox"
                                      disabled={
                                        userAtom.businessid !== 995 &&
                                        Boolean(businessHasOauthedWithOutreach)
                                      }
                                      handleChange={() => {
                                        formik.setFieldValue(
                                          'accountSettings.requireOutcomeForAllAgents',
                                          !formik.values.accountSettings
                                            .requireOutcomeForAllAgents
                                        );
                                        const isOn =
                                          formik.values.accountSettings
                                            .requireOutcomeForAllAgents;
                                        if (!isOn) {
                                          formik.setFieldValue(
                                            'accountSettings.showOutcomeForAllAgents',
                                            true
                                          );
                                        } else {
                                          formik.setFieldValue(
                                            'accountSettings.showOutcomeForAllAgents',
                                            showOutcomeForAllAgentsVal
                                          );
                                        }
                                      }}
                                    />
                                  }
                                  label="Require Outcome for All Agents"
                                />
                              </div>
                            </div>
                          </LabelledOutline>
                        )}
                      </div>
                      {isMachineDetectionSectionVisible && (
                        <div className="row">
                          <div className="col-lg-8">
                            <h5 className="fieldTitle">
                              PowerDialer AI Human Detect
                            </h5>
                            <p className="fieldDescription">
                              Timeout duration dictates the length of time to
                              spend detecting a machine on outbound PowerDialer
                              calls. Choosing a longer duration increases the
                              accuracy of the AI Human Detect capability. A
                              shorter duration reduces the time a human waits
                              before being connected to an agent.
                            </p>
                          </div>
                          <div className="col-lg-4">
                            <FormikSelect
                              name="accountSettings.machineDetectionTime"
                              data-qa="machine-detection-time"
                              formik={formik}
                              label="Timeout Duration (seconds)"
                              size="small"
                              variant="outlined"
                              className="w-100"
                            >
                              <MenuItem value="2000">2</MenuItem>
                              <MenuItem value="2500">2.5</MenuItem>
                              <MenuItem value="3000">3</MenuItem>
                              <MenuItem value="3500">3.5</MenuItem>
                              <MenuItem value="4000">4</MenuItem>
                              <MenuItem value="4500">4.5</MenuItem>
                              <MenuItem value="5000">5</MenuItem>
                            </FormikSelect>
                          </div>
                        </div>
                      )}
                    </FormGroup>
                  </TabPanel>
                  <TabPanel value="2">
                    {/* Manager Options */}
                    <div className="row borderBottom">
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">
                          Restrict Reporting to Only Managers
                        </h5>
                        <p className="fieldDescription">
                          Toggle whether non-manager and non-admin agents on
                          your account can view the Reporting section of the
                          Kixie dashboard.
                        </p>
                      </div>
                      <div className="col-lg-4">
                        <FormControlLabel
                          control={
                            <FormikSwitch
                              name="accountSettings.onlyManagersCanViewCallReporting"
                              data-qa="restrict-reporting-to-only-managers"
                              formik={formik}
                              color="primary"
                              type="checkbox"
                            />
                          }
                          label="Restrict Reporting to Only Managers"
                        />
                      </div>
                    </div>
                    <div className="row borderBottom">
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">
                          Restrict Add Numbers to Only Managers
                        </h5>
                        <p className="fieldDescription">
                          Toggle whether agents can purchase new numbers on your
                          account for use either as a personal number or to be
                          associated with an entity like Queues or Powerlists.
                        </p>
                      </div>
                      <div className="col-lg-4">
                        <FormControlLabel
                          style={{ maxWidth: '90%' }}
                          control={
                            <FormikSwitch
                              name="accountSettings.onlyManagersCanAddPhoneNumbers"
                              data-qa="restrict-add-numbers-to-only-managers"
                              formik={formik}
                              color="primary"
                              type="checkbox"
                            />
                          }
                          label="Restrict Add Numbers to Only Managers"
                        />
                      </div>
                    </div>
                    {canAccessPowerlistProduct && (
                      <div className="row borderBottom">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">
                            Allow Agents to Create Powerlists
                          </h5>
                          <p className="fieldDescription">
                            Toggle whether agents can create Personal Powerlists
                            and upload their own list of leads to dial outside
                            of Organizational Powerlists. These are visible to
                            the agent under My Profile and can be viewed by
                            Managers under Manage Agents.
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <FormControlLabel
                            control={
                              <FormikSwitch
                                name="accountSettings.allowAgentPowerlist"
                                data-qa="allow-agents-to-create-powerlists"
                                formik={formik}
                                color="primary"
                                type="checkbox"
                                onClick={() => {
                                  // Only show confirmChange modal if the iniital allowAgentPowerlists value is true
                                  if (
                                    initialAllowAgentPowerlists === 1 &&
                                    formik.values.accountSettings
                                      .allowAgentPowerlist
                                  ) {
                                    setShowConfirmHandlePowerlists(true);
                                  }
                                }}
                              />
                            }
                            label="Allow Agents to Create Powerlists"
                          />
                        </div>
                      </div>
                    )}
                    <div
                      className={`row ${
                        role === Role.SUPERADMIN ? 'borderBottom' : ''
                      }`}
                    >
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">
                          Allow Webhooks on Account
                        </h5>
                        <p className="fieldDescription">
                          Enable or disable the use of webhooks to allow custom
                          integrations, automations, and workflows.
                        </p>
                      </div>
                      <div className="col-lg-4">
                        <FormControlLabel
                          control={
                            <FormikSwitch
                              name="accountSettings.hasWebhook"
                              data-qa="allow-webhooks-on-account"
                              formik={formik}
                              color="primary"
                              type="checkbox"
                            />
                          }
                          label="Allow Webhooks on Account"
                        />
                      </div>
                    </div>
                    {role === Role.SUPERADMIN && isDeveloper && (
                      <div className="row superStyle borderBottom">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">Allow Ring on Held</h5>
                          <p className="fieldDescription">
                            Ring when a call is being held
                            <span
                              style={{
                                display: 'block',
                                color: 'red',
                              }}
                            >
                              NOTE: turning off "Allow Ring on Held" still rings
                              agent for 1 second before auto-rejecting.
                            </span>
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <FormControlLabel
                            control={
                              <FormikSwitch
                                name="accountSettings.allowRingOnHeld"
                                data-qa="allow-ring-on-held"
                                formik={formik}
                                color="primary"
                                type="checkbox"
                              />
                            }
                            label="Allow Ring on Held"
                          />
                        </div>
                      </div>
                    )}
                  </TabPanel>
                  <TabPanel value="3">
                    {/* Integrations */}
                    <FormikTextField
                      className={classes.field}
                      formik={formik}
                      name="id"
                      fullWidth
                      label="Business Id"
                      data-qa="business-id-modal"
                      InputProps={{
                        readOnly: true,
                      }}
                      size="small"
                      variant="outlined"
                    />
                    <FormikTextField
                      className={classes.field}
                      formik={formik}
                      name="api"
                      data-qa="api-key-modal"
                      fullWidth
                      label="API Key"
                      InputProps={{
                        readOnly: true,
                      }}
                      size="small"
                      variant="outlined"
                    />
                  </TabPanel>
                  {crmStatsMemo?.length && (
                    <TabPanel value="4">
                      {/* Connected CRMs */}
                      <div style={{ margin: '0px 24px' }}>
                        {crmStatsMemo?.length ? (
                          (crmStatsMemo ?? []).map((elem, elemKey) => {
                            return (
                              <div
                                key={elemKey}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  borderBottom: '1px solid #eeeeee',
                                  padding: '15px 0',
                                }}
                              >
                                <div
                                  style={{
                                    margin: '-50px 0 0',
                                    width: '120px',
                                    maxWidth: '120px',
                                    minWidth: '120px',
                                  }}
                                >
                                  <div>
                                    <img
                                      style={{
                                        borderRadius: '50%',
                                        height: '72px',
                                        width: '72px',
                                        border: '1px solid #eeeeee',
                                        boxShadow:
                                          'rgba(184, 184, 184, 0.34) 0px 0px 20px -2px',
                                      }}
                                      src={getCRMIcon(elem?.crmName)}
                                    />
                                  </div>
                                </div>
                                <div
                                  style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    width: '70%',
                                  }}
                                >
                                  <div className={classes.fourCol}>
                                    <p className={classes.mutedHeader}>
                                      Connected CRM
                                    </p>
                                    {getCRMName(elem.crmName)}
                                  </div>
                                  {!!(elem.orgid || elem.location) && (
                                    <div className={classes.fourCol}>
                                      <p className={classes.mutedHeader}>
                                        Account ID
                                      </p>
                                      {elem.orgid ? elem.orgid : elem.location}
                                    </div>
                                  )}
                                  {!!elem.countUsers && (
                                    <div className={classes.fourCol}>
                                      <p className={classes.mutedHeader}>
                                        Number of Users
                                      </p>
                                      {elem.countUsers}
                                    </div>
                                  )}
                                  {!!elem.calleridName && (
                                    <div className={classes.fourCol}>
                                      <p className={classes.mutedHeader}>
                                        Connected Admin
                                      </p>
                                      {elem.calleridName}
                                    </div>
                                  )}
                                  {!!elem.created && (
                                    <div className={classes.fourCol}>
                                      <p className={classes.mutedHeader}>
                                        Connected Date
                                      </p>
                                      {formatDate(elem.created)}
                                    </div>
                                  )}
                                  {!!elem.totalSynced && (
                                    <div className={classes.fourCol}>
                                      <p className={classes.mutedHeader}>
                                        Contacts Synced
                                      </p>
                                      {elem.totalSynced}
                                    </div>
                                  )}
                                  {!!elem.status && (
                                    <div className={classes.fourCol}>
                                      <p className={classes.mutedHeader}>
                                        Sync Status
                                      </p>
                                      {elem.status}
                                    </div>
                                  )}
                                  {!!elem.startDate && (
                                    <div className={classes.fourCol}>
                                      <p className={classes.mutedHeader}>
                                        Sync Duration
                                      </p>
                                      {calculateTimeDifference(
                                        elem.startDate,
                                        elem.endDate
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div>No CRMs Connected</div>
                        )}
                      </div>
                    </TabPanel>
                  )}
                  <TabPanel value="5">
                    {/* Business Hours */}
                    {businessLoadable.state !== 'hasValue' ? (
                      <>Loading...</>
                    ) : (
                      <>
                        <LabelledOutline label="Business Hours">
                          <div className="row">
                            <div
                              data-qa="business-hours-set-for-business"
                              className="col-lg-6"
                            >
                              {splitHours.map((string, index) => (
                                <div key={index}>
                                  {string} <br />
                                </div>
                              ))}
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                paddingRight: '50px',
                              }}
                              className="col-lg-6"
                            >
                              <Button
                                variant="contained"
                                color="primary"
                                className={classes.editButton}
                                data-qa="business-hours-edit-button"
                                onClick={handleShowBusinessHours}
                              >
                                Edit Business Hours
                              </Button>
                            </div>
                          </div>
                        </LabelledOutline>
                        <br />
                        <br />
                        <br />
                        <ModalWithStyles
                          onSubmit={handleSubmitBusinessHours}
                          title={'Business Hours'}
                          show={showBusinessHours}
                          onHide={onHideBusinessHours}
                          isSubmitting={formik.isSubmitting}
                          size="lg"
                        >
                          <Scheduler
                            setSchedulableHours={handleSetSchedulableHours}
                            //insert formik value aki
                            schedulableHours={formik.values.businessHours}
                          />
                        </ModalWithStyles>
                      </>
                    )}
                  </TabPanel>
                  <TabPanel value="6">
                    {/* Harassment Rules */}
                    <HarassmentRules formik={formik as any} />
                  </TabPanel>
                  {isCrmPropertiesFeatureEnabled && (
                    <TabPanel value="7">
                      {/* CRM Properties */}
                      <>
                        <div className="row">
                          <div className="col-lg-8 row">
                            <div className="col-lg-6">
                              <FormikSelect
                                name="crmProperties.crm"
                                style={{ width: '100%', minWidth: '150px' }}
                                className={classes.outbound2}
                                formik={formik}
                                label="CRM"
                                data-qa="crm-properties-name"
                                margin="dense"
                                size="small"
                                variant="outlined"
                                onChange={(e) => {
                                  setActiveCRM(CRMIDMap[e.target.value]);
                                }}
                              >
                                {activeCRMs?.length &&
                                  activeCRMs.map(
                                    (
                                      crm: {
                                        value: string;
                                        displayName: string;
                                      },
                                      index: number
                                    ) => {
                                      return (
                                        <MenuItem value={crm.value} key={index}>
                                          {crm.displayName}
                                        </MenuItem>
                                      );
                                    }
                                  )}
                              </FormikSelect>
                            </div>
                            <div className="col-lg-6">
                              <TextField
                                disabled={true}
                                style={{
                                  width: '100%',
                                  marginTop: '8px',
                                }}
                                // formik={formik}
                                value={lastUpdatedDate}
                                // name="crmProperties.lastUpdatedDate"
                                label="Last Update"
                                data-qa="last-updated"
                                // type="date"
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                size="small"
                              />
                            </div>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                            }}
                            className="col-lg-4"
                          >
                            <Button
                              data-qa="get-crm-properties"
                              variant="contained"
                              color="primary"
                              style={{ height: '36px', marginTop: '8px' }}
                              disabled={formik.isSubmitting || importLoading}
                              // className={classes.editButton}
                              onClick={(_e) => {
                                importCRMPropertiesHandler();
                              }}
                            >
                              Get CRM Properties
                            </Button>
                          </div>
                        </div>
                        <hr style={{ marginTop: '24px' }} />
                        <p
                          style={{ fontSize: '1rem', margin: '0px' }}
                        >{`Available ${activeCRMName} Properties`}</p>
                        <DataTable
                          tabTable={true}
                          hideCsv={true}
                          setSelectedRowsSearch={setSelectedRowsSearch}
                          scrollable={true}
                          ToolbarComponent={ToolbarComponent}
                          rows={formik.values.crmProperties.rows ?? []}
                          columns={columns}
                          sortModel={[{ field: 'property', sort: 'asc' }]}
                          borderOutline={true}
                          // ToolbarComponent={ToolbarComponent}
                          loading={
                            getBusinessCRMPropertiesLoadable.state ===
                              'loading' || importLoading
                          }
                        />
                      </>
                    </TabPanel>
                  )}
                  {isAIOEnabled && (
                    <TabPanel value={AIO_SETTINGS_TAB_INDEX}>
                      {/* AI Orchestration */}
                      <AIOSettingsTab
                        formik={formik}
                        userAtom={userAtom}
                        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
                      />
                    </TabPanel>
                  )}
                  <TabPanel value={CUSTOM_CRM_TAB_INDEX}>
                    {/* Custom CRM */}
                    <CustomCRM
                      formik={formik}
                      setValidationOfCustomCRM={setValidationOfCustomCRM}
                    />
                  </TabPanel>
                  <TabPanel value={LOCAL_PRESENCE_ROUTING_TAB_INDEX}>
                    {/* Connection Boost Routing */}
                    <Suspense fallback={<DefaultActionSkeleton />}>
                      <DefaultAction
                        data-qa="local-presence-subaction"
                        defaultAction={getDefaultActionUrl({
                          ...formik.values.localPresencePremiumActionPayload,
                          parentType: 'user',
                          voicemail: String(userAtom.userid),
                          businessid: userAtom.businessid,
                        })}
                        label="Local Presence Action"
                        actionType={
                          formik.values.localPresencePremiumActionPayload.type
                        }
                        typeName="localPresencePremiumActionPayload.type"
                        ivrName="localPresencePremiumActionPayload.ivr"
                        groupName="localPresencePremiumActionPayload.group"
                        userName="localPresencePremiumActionPayload.user"
                        queueName="localPresencePremiumActionPayload.queue"
                        formik={formik}
                        showVoicemail={false}
                      />
                    </Suspense>
                  </TabPanel>
                  {isCallTranscriptionsFeaturedEnabled && (
                    <TabPanel value={CALL_TRANSCRIPTION_SETTINGS_TAB_INDEX}>
                      <div className="row">
                        <Subcontainer
                          data-qa="conversation-strength-calculator"
                          title="Conversation Strength Calculator"
                          show={true}
                          className="col-lg-8"
                          labelLeft={12}
                        >
                          <FormControlLabel
                            label="Keywords"
                            className={classes.checkbox}
                            control={
                              <Checkbox
                                data-qa="conversation-strength-calculator-keywords"
                                name="callTranscriptionSettings.isCallStrengthKeywordsEnabled"
                                color="primary"
                                checked={isCallStrengthKeywordsEnabled}
                                onChange={(e) => {
                                  formik.setFieldValue(
                                    e.target.name,
                                    e.target.checked
                                  );
                                  toggleCallStrengthKeywordsEnabled();
                                }}
                              />
                            }
                          />
                          <FormControlLabel
                            label="Phrases"
                            className={classes.checkbox}
                            control={
                              <Checkbox
                                data-qa="conversation-strength-calculator-phrases"
                                name="callTranscriptionSettings.isCallStrengthPhrasesEnabled"
                                color="primary"
                                checked={isCallStrengthPhrasesEnabled}
                                onChange={(e) => {
                                  formik.setFieldValue(
                                    e.target.name,
                                    e.target.checked
                                  );
                                  toggleCallStrengthPhrasesEnabled();
                                }}
                              />
                            }
                          />
                          <FormControlLabel
                            label="Call Sentiment"
                            className={classes.checkbox}
                            control={
                              <Checkbox
                                data-qa="conversation-strength-calculator-call-sentiment"
                                name="callTranscriptionSettings.isCallStrengthCallSentimentEnabled"
                                color="primary"
                                checked={isCallStrengthCallSentimentEnabled}
                                onChange={(e) => {
                                  formik.setFieldValue(
                                    e.target.name,
                                    e.target.checked
                                  );
                                  toggleCallStrengthCallSentimentEnabled();
                                }}
                              />
                            }
                          />
                        </Subcontainer>
                        <Subcontainer
                          title="Min. Conversation Duration"
                          show={true}
                          className="col-lg-4"
                          labelLeft={12}
                        >
                          <FormikTextField
                            name="callTranscriptionSettings.minimumDuration"
                            className={classes.field}
                            formik={formik}
                            style={{
                              marginRight: '8px',
                              flexGrow: 1,
                              width: '100%',
                            }}
                            label="Minutes"
                            size="small"
                            type="number"
                            value={
                              formik.values.callTranscriptionSettings
                                .minimumDuration
                            }
                            InputProps={{
                              inputProps: {
                                min: CALL_TRANSCRIPTION_MIN_CALL_DURATION_MINUTES,
                                max: CALL_TRANSCRIPTION_MAX_CALL_DURATION_MINUTES,
                              },
                              onChange: (e) => {
                                formik.handleChange(e);
                                updateMinimumDuration(e);
                              },
                            }}
                            variant="outlined"
                            error={
                              !!(formik.errors as any).callTranscriptionSettings
                                ?.minimumDuration
                            }
                          />
                        </Subcontainer>
                      </div>
                      <div className="row">
                        <Subcontainer
                          title="Phrases"
                          show={true}
                          className={`col-lg-8 ${classes.listContainer}`}
                          labelLeft={12}
                          minHeight={274}
                        >
                          <ul className={classes.list}>
                            {Object.values(phrases ?? {}).map(
                              ({ id, text, isEditing }) => (
                                <li
                                  key={`phrase-${id}`}
                                  className={classes.listItem}
                                >
                                  <TextField
                                    value={text}
                                    multiline
                                    disabled={!isEditing}
                                    className={classes.listItemTextField}
                                    autoFocus
                                    onChange={(e) => {
                                      formik.setFieldValue(
                                        'callTranscriptionSettings.phrases',
                                        updatePhrase(e, id)
                                      );
                                    }}
                                    variant="standard"
                                  />
                                  <div className={classes.controls}>
                                    <IconButton
                                      color="primary"
                                      className={classes.editBtn}
                                      onClick={() => {
                                        formik.setFieldValue(
                                          'callTranscriptionSettings.phrases',
                                          editPhrase(id)
                                        );
                                      }}
                                      disabled={!isValidPhrase(text)}
                                      size="large"
                                    >
                                      {isEditing ? <Done /> : <Edit />}
                                    </IconButton>
                                    <IconButton
                                      className={classes.deleteBtn}
                                      onClick={() => {
                                        formik.setFieldValue(
                                          'callTranscriptionSettings.phrases',
                                          deletePhrase(id)
                                        );
                                      }}
                                      size="large"
                                    >
                                      <Delete />
                                    </IconButton>
                                  </div>
                                </li>
                              )
                            )}
                            <li>
                              <FormControlLabel
                                data-qa="add-phrase"
                                label="Add Phrase"
                                className={classes.addBtn}
                                control={
                                  <IconButton
                                    color="primary"
                                    onClick={() => {
                                      formik.setFieldValue(
                                        'callTranscriptionSettings.phrases',
                                        addPhrase()
                                      );
                                    }}
                                    size="large"
                                  >
                                    <Add />
                                  </IconButton>
                                }
                              />
                            </li>
                          </ul>
                          {Object.entries(
                            (formik.errors as any)?.callTranscriptionSettings
                              ?.phrases ?? {}
                          )
                            .filter(
                              ([_id, value]) =>
                                Object.keys(value ?? {}).filter(
                                  (k) => k === 'text'
                                ).length > 0
                            )
                            .filter(([id, _value]) => phrases?.[id]?.touched)
                            .length > 0 ? (
                            <FormHelperText error={true}>
                              Must be between 1 and{' '}
                              {CALL_TRANSCRIPTION_MAX_PHRASE_CHARACTERS}{' '}
                              characters long
                            </FormHelperText>
                          ) : null}
                        </Subcontainer>
                        <Subcontainer
                          title="Keywords"
                          show={true}
                          className={`col-lg-4 ${classes.listContainer}`}
                          labelLeft={12}
                          minHeight={274}
                        >
                          <ul className={classes.list}>
                            {Object.values(keywords ?? {}).map(
                              ({ id, text, isEditing }) => (
                                <li
                                  key={`keyword-${id}`}
                                  className={classes.listItem}
                                >
                                  <TextField
                                    value={text}
                                    multiline
                                    disabled={!isEditing}
                                    className={classes.listItemTextField}
                                    autoFocus
                                    onChange={(e) => {
                                      formik.setFieldValue(
                                        'callTranscriptionSettings.keywords',
                                        updateKeyword(e, id)
                                      );
                                    }}
                                    variant="standard"
                                  />
                                  <div className={classes.controls}>
                                    <IconButton
                                      color="primary"
                                      className={classes.editBtn}
                                      onClick={() => {
                                        formik.setFieldValue(
                                          'callTranscriptionSettings.keywords',
                                          editKeyword(id)
                                        );
                                      }}
                                      disabled={!isValidKeyword(text)}
                                      size="large"
                                    >
                                      {isEditing ? <Done /> : <Edit />}
                                    </IconButton>
                                    <IconButton
                                      className={classes.deleteBtn}
                                      onClick={() => {
                                        formik.setFieldValue(
                                          'callTranscriptionSettings.keywords',
                                          deleteKeyword(id)
                                        );
                                      }}
                                      size="large"
                                    >
                                      <Delete />
                                    </IconButton>
                                  </div>
                                </li>
                              )
                            )}
                            <li>
                              <FormControlLabel
                                data-qa="add-keywords"
                                label="Add Keyword"
                                className={classes.addBtn}
                                control={
                                  <IconButton
                                    color="primary"
                                    onClick={() => {
                                      formik.setFieldValue(
                                        'callTranscriptionSettings.keywords',
                                        addKeyword()
                                      );
                                    }}
                                    size="large"
                                  >
                                    <Add />
                                  </IconButton>
                                }
                              />
                            </li>
                          </ul>
                          {Object.entries(
                            (formik.errors as any)?.callTranscriptionSettings
                              ?.keywords ?? {}
                          )
                            .filter(
                              ([_id, value]) =>
                                Object.keys(value ?? {}).filter(
                                  (k) => k === 'text'
                                ).length > 0
                            )
                            .filter(([id, _value]) => keywords?.[id]?.touched)
                            .length > 0 ? (
                            <FormHelperText error={true}>
                              Must be between 1 and{' '}
                              {CALL_TRANSCRIPTION_MAX_KEYWORD_CHARACTERS}{' '}
                              characters long
                            </FormHelperText>
                          ) : null}
                        </Subcontainer>
                      </div>
                    </TabPanel>
                  )}

                  {role === Role.SUPERADMIN && (
                    <TabPanel value={SUPER_ADMIN_SETTINGS_TAB_INDEX}>
                      {/* Super Admin Settings */}
                      <div className="row">
                        <Subcontainer
                          title="Account Settings"
                          show={true}
                          className="col-lg-8"
                          labelLeft={12}
                          minHeight={184}
                        >
                          <div>
                            <MaterialAutocomplete
                              style={{ flexGrow: 1, marginBottom: '8px' }}
                              type="text"
                              fullWidth
                              label="Default CRM User"
                              size="small"
                              variant="outlined"
                              multiple={false}
                              options={businessUsersActiveCached.current ?? []}
                              required={!!businessData?.defaultCrmUserid}
                              value={defaultCrmUser}
                              onChange={(event, newValue) =>
                                formik.setFieldValue(
                                  'defaultCrmUserid',
                                  newValue ? newValue.userid : ''
                                )
                              }
                              getOptionLabel={(option) =>
                                option
                                  ? `${option?.fname} ${option?.lname}`
                                  : ''
                              } // collapse to empty string if no option found
                              isOptionEqualToValue={(option, value) =>
                                option?.userid === value?.userid
                              }
                            />
                          </div>
                          <div className="row">
                            <div className="col-lg-6">
                              <FormControlLabel
                                control={
                                  <FormikSwitch
                                    name="accountSettings.canDeleteUsers"
                                    formik={formik}
                                    color="primary"
                                    type="checkbox"
                                  />
                                }
                                label="Can Delete Users"
                              />
                            </div>
                            <div className="col-lg-6">
                              <FormControlLabel
                                control={
                                  <FormikSwitch
                                    name="accountSettings.DncBlocksOutboundSMS"
                                    formik={formik}
                                    color="primary"
                                    type="checkbox"
                                  />
                                }
                                label="Block Outbound SMS"
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-lg-6">
                              <FormControlLabel
                                control={
                                  <FormikSwitch
                                    name="accountSettings.autoAnswerApi"
                                    formik={formik}
                                    color="primary"
                                    type="checkbox"
                                  />
                                }
                                label="Auto Answer Call Event"
                              />
                            </div>
                            <div className="col-lg-6">
                              <FormControlLabel
                                control={
                                  <FormikSwitch
                                    name="accountSettings.stopBlocksOutboundCalls"
                                    formik={formik}
                                    color="primary"
                                    type="checkbox"
                                  />
                                }
                                label="Block Outbound Calls"
                              />
                            </div>
                          </div>
                        </Subcontainer>
                        <Subcontainer
                          labelLeft={12}
                          minHeight={184}
                          title="API Settings"
                          show={true}
                          className="col-lg-4"
                        >
                          <div
                            className={'row'}
                            style={{
                              justifyContent: 'center',
                              marginTop: '8px',
                            }}
                          >
                            <div className={classes.alignCenter}>
                              <FormControlLabel
                                control={
                                  <FormikSwitch
                                    name="accountSettings.apiKeyEnabled"
                                    formik={formik}
                                    color="primary"
                                    type="checkbox"
                                  />
                                }
                                label="API Key Enabled"
                              />
                            </div>
                          </div>
                          {canSetApiLevel &&
                            formik.values.accountSettings.apiKeyEnabled &&
                            !!usagePlansToDisplay.length && (
                              <div
                                className={'row'}
                                style={{
                                  justifyContent: 'center',
                                  width: 'auto',
                                  marginLeft: '0px',
                                  marginRight: '0px',
                                }}
                              >
                                {formik.values.accountSettings.apiKeyEnabled &&
                                  usagePlansToDisplay.length && (
                                    <div className={classes.fullWidthSelect}>
                                      <FormikSelect
                                        style={{
                                          display: 'flex',
                                          width: '100%',
                                          minWidth: '160px',
                                        }}
                                        name="accountSettings.usagePlanProduct"
                                        formik={formik}
                                        label="API Level"
                                        size="small"
                                        variant="outlined"
                                      >
                                        {usagePlansToDisplay.map(
                                          (plan, index) =>
                                            plan.active === 1 && (
                                              <MenuItem
                                                key={index}
                                                value={plan.product}
                                              >
                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    gap: '2px',
                                                    fontSize: '.87rem',
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      minWidth: '82px',
                                                    }}
                                                  >
                                                    {plan.quotaLimit.toLocaleString(
                                                      'en-US'
                                                    )}{' '}
                                                    per {plan.quotaPeriod}
                                                  </div>
                                                  <div>
                                                    (
                                                    {new Intl.NumberFormat(
                                                      'en-US',
                                                      {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                      }
                                                    ).format(
                                                      plan.perUseproductid ?? 0
                                                    )}
                                                    /mo.)
                                                  </div>
                                                </div>
                                              </MenuItem>
                                            )
                                        )}
                                      </FormikSelect>
                                    </div>
                                  )}
                              </div>
                            )}
                        </Subcontainer>
                      </div>
                      <div
                        className={'row'}
                        style={{ marginTop: '24px', marginBottom: '8px' }}
                      >
                        <Subcontainer
                          minHeight={216}
                          labelLeft={12}
                          title="Discount Settings"
                          show={true}
                          className="col-lg-8"
                        >
                          <div className="row">
                            <div className="col-lg-6">
                              <FormikTextField
                                name="accountSettings.businessDiscount"
                                className={classes.field}
                                formik={formik}
                                label="Discount %"
                                style={{
                                  marginBottom: '16px',
                                  width: '100%',
                                  flexGrow: 1,
                                }}
                                InputProps={{
                                  inputProps: {
                                    min: 0,
                                    max: 60,
                                  },
                                }}
                                size="small"
                                type="number"
                                variant="outlined"
                                onInput={(event: any) => {
                                  if (
                                    !event?.target?.value ||
                                    Number.parseInt(event?.target?.value) === 0
                                  ) {
                                    setShowDiscountEndDate(false);
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-lg-6">
                              {process.env.VITE_APP_DISCOUNT_DURATION ===
                                '1' && (
                                <div>
                                  <div style={{ display: 'flex' }}>
                                    <RadioColumn
                                      clickHandler={(e) => {
                                        formik.setFieldValue(
                                          'accountSettings.discountEndDate',
                                          null
                                        );
                                        setShowDiscountEndDate(false);
                                      }}
                                      on={!showDiscountEndDate}
                                      color="blue"
                                      tooltip="Perpetual (No End Date)"
                                      disabled={false}
                                    />
                                    <p
                                      style={{
                                        marginLeft: '4px',
                                        marginBottom: '0px',
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      Perpetual (No End Date)
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-lg-6">
                              {process.env.VITE_APP_DISCOUNT_DURATION ===
                                '1' && (
                                <div>
                                  <div style={{ display: 'flex' }}>
                                    <RadioColumn
                                      clickHandler={(e) => {
                                        if (
                                          !formik.values.accountSettings
                                            .businessDiscount ||
                                          formik.values.accountSettings.businessDiscount.toString() ===
                                            '0'
                                        ) {
                                          setShowDiscountEndDate(false);
                                        } else {
                                          setShowDiscountEndDate(true);
                                        }
                                      }}
                                      on={showDiscountEndDate}
                                      color="blue"
                                      tooltip="Has End Date"
                                      disabled={
                                        !formik.values.accountSettings
                                          .businessDiscount ||
                                        formik.values.accountSettings.businessDiscount.toString() ===
                                          '0'
                                      }
                                    />
                                    <p
                                      style={{
                                        marginLeft: '4px',
                                        marginBottom: '0px',
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      Has End Date
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="row" style={{ marginBottom: '8px' }}>
                            <div className="col-lg-6">
                              {process.env.VITE_APP_DISCOUNT_DURATION === '1' &&
                                showDiscountEndDate && (
                                  <div>
                                    <FormikTextField
                                      style={{
                                        width: '100%',
                                        marginTop: '16px',
                                      }}
                                      formik={formik}
                                      name="accountSettings.discountEndDate"
                                      label="Discount % End Date"
                                      type="date"
                                      InputLabelProps={{ shrink: true }}
                                      variant="outlined"
                                      size="small"
                                      inputProps={{
                                        min: currentDate,
                                      }}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>
                        </Subcontainer>
                        <Subcontainer
                          labelLeft={12}
                          minHeight={216}
                          title="Deal Settings"
                          show={true}
                          className="col-lg-4"
                        >
                          <div className="row">
                            <div style={{ width: '100%', margin: '0px 12px' }}>
                              <FormikSelect
                                style={{
                                  flexGrow: 1,
                                  marginBottom: '4px',
                                  width: '100%',
                                }}
                                name="accountSettings.businessBillingTerms"
                                className={classes.billing}
                                disabled={!!businessData?.dealClosedBy}
                                formik={formik}
                                label="Billing Terms"
                                size="small"
                                variant="outlined"
                              >
                                <MenuItem value="1">1</MenuItem>
                                <MenuItem value="3">3</MenuItem>
                                <MenuItem value="6">6</MenuItem>
                                <MenuItem value="12">12</MenuItem>
                                <MenuItem value="24">24</MenuItem>
                              </FormikSelect>
                            </div>
                          </div>
                          <div className="row">
                            <div style={{ width: '100%', margin: '0px 12px' }}>
                              <FormikSelect
                                style={{
                                  flexGrow: 1,
                                  marginBottom: '12px',
                                  width: '100%',
                                }}
                                disabled={!!businessData?.dealClosedBy}
                                name="accountSettings.businessContractTerms"
                                className={classes.billing}
                                formik={formik}
                                label="Contract Terms"
                                size="small"
                                variant="outlined"
                              >
                                <MenuItem value="1">1</MenuItem>
                                <MenuItem value="3">3</MenuItem>
                                <MenuItem value="6">6</MenuItem>
                                <MenuItem value="12">12</MenuItem>
                                <MenuItem value="24">24</MenuItem>
                                <MenuItem value="36">36</MenuItem>
                              </FormikSelect>
                            </div>
                          </div>
                          <div className="row">
                            {businessData?.dealClosedBy ? (
                              <p
                                style={{ fontSize: '11px', margin: '8px 12px' }}
                              >{`Deal Closed on ${dealClosedDate} by ${dealClosedBy}`}</p>
                            ) : (
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  margin: '12px 12px 0px',
                                  width: '100%',
                                }}
                              >
                                <Tooltip
                                  title={
                                    billingEmailLoadable?.contents?.response
                                      ?.data?.providerexternalid
                                      ? ''
                                      : `Stripe account doesn't exist. Customer needs to add credit card.`
                                  }
                                >
                                  <div>
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      disabled={
                                        !billingEmailLoadable?.contents
                                          ?.response?.data?.providerexternalid
                                      }
                                      className={classes.dealButton}
                                      onClick={openCloseDealConfirmation}
                                    >
                                      Close Deal
                                    </Button>
                                  </div>
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        </Subcontainer>
                      </div>
                      <ModalWithStyles
                        submitLabel="Continue"
                        onSubmit={async () => {
                          setSubmittingCloseDeal(true);
                          const data = {
                            call: methodNameEnum.CLOSE_DEAL_BY_BUSINESS_ID,
                            apikey: userAtom?.apikey,
                            user: userAtom?.user?.calleridName,
                            businessid: userAtom?.businessid,
                            businessbillingterms:
                              formik.values.accountSettings
                                .businessBillingTerms,
                            businesscontractterms:
                              formik.values.accountSettings
                                .businessContractTerms,
                          };
                          const closeDealResults = await mutation.mutateAsync({
                            endpoint: endpointEnum.CLOSE_DEAL_BY_BUSINESS_ID,
                            payload: data,
                          });
                          if (closeDealResults?.data?.success) {
                            enqueueSnackbarWithStyles({
                              message: 'Successfully closed deal.',
                              alertProps: { severity: 'success' },
                            });
                          } else {
                            enqueueSnackbarWithStyles({
                              message: 'There was an issue closing the deal.',
                              alertProps: { severity: 'error' },
                            });
                          }
                          setRefetchToggleForBusinessData({});
                          setRefetchToggleForBusinessDataV1({});
                          setSubmittingCloseDeal(false);
                          hideCloseDealConfirmation();
                        }}
                        isLoading={submittingCloseDeal || formik.isSubmitting}
                        isSubmitting={
                          submittingCloseDeal || formik.isSubmitting
                        }
                        title="Close Deal Confirmation"
                        aria-labelledby="example-modal-sizes-title-md"
                        show={showCloseDealConfirmation}
                        onHide={hideCloseDealConfirmation}
                        // isLoading={formik.isSubmitting}
                        size="xs"
                      >
                        <p>
                          Clicking Continue will proceed in closing this deal.
                          Please note this is irreversible! Only proceed once
                          you are sure you're ready to close the deal.
                        </p>
                        <p>Click Cancel to go back.</p>
                      </ModalWithStyles>
                    </TabPanel>
                  )}
                  {!formik.isSubmitting && // submitting turns all fields to touched
                    (formikTouched.current = formik.touched) && <></>}
                </WizardModal>
              </TabContext>
              {/* </ModalWithStyles> */}
              <ConfirmAgentPowerlists
                // handleClose={handleCloseDelete}
                handleClose={() => {
                  setShowConfirmHandlePowerlists(false);
                  if (
                    formik.values.accountSettings.allowAgentPowerlist === false
                  ) {
                    formik.setFieldValue(
                      'accountSettings.allowAgentPowerlist',
                      true
                    );
                  }
                }}
                handleSubmit={() => {
                  setShowConfirmHandlePowerlists(false);
                }}
                show={showConfirmAgentPowerlists}
              />
            </>
          );
        }}
      </Formik>
    </Paper>
  );
}
